import * as THREE from 'three';
import { animatableProps } from '../core/anim.js';
import { MAX_TILES, NICE, SHAPES } from '../core/doc.js';
import { isOrbitLike, isSiteLike } from '../scene/cameras.js';
import { recessBorders } from '../scene/geometry.js';
import { cameraObj, eyeVec, rebuildHelpers } from '../scene/helpers.js';
import { wallOf } from '../scene/panels.js';
import { eraseOf, revealOf } from '../scene/textarea.js';
import { applyEnvIntensity, applyLighting, effectiveLightAzEl, key, setShadowSoft } from '../scene/lighting.js';
import { BLEND_NAMES, aimEuler, applyLook, applyReveal, canPivot, deleteSel, duplicateSel, getSel, linkObjects, moveObject, refitFrame, refitRecess, rescaleForScreen, select, setPivot, syncNode, syncTransform, parentToNewNull, unlinkObject, wouldCreateCycle } from '../scene/objects.js';
import { fitStage } from '../scene/renderer.js';
import { emit, on, requestRender } from '../state/bus.js';
import { drawPlan, syncPlan } from './plan-view.js';
import { histOn, pushHistory } from '../state/history.js';
import { applyAnimation, captureKey, quantise, setPlayhead } from '../state/timeline.js';
import { $, S, clamp, el, fmt } from '../state/store.js';
import { markStale, placementOnly, syncCalibFoot } from '../ui/calibrate-view.js';
import { goStage, setView } from '../ui/chrome.js';
import { EYE_OFF, EYE_ON, ICONS, captureFocus, checkRow, colorRow, deferRail, fieldRow, infoToggle, numRow, pairRow, panel, isEditing, readout, restoreFocus, scrubbing, segRow, selectRow, sliderRow, stackRow, tabStrip, transformGrid, vec3Row } from '../ui/controls.js';
import { MOTION_CTA, canUseScreenArray } from '../services/limits.js';
import { openMask } from '../ui/mask-editor.js';
import { showTrial } from '../ui/trial.js';
import { toast } from '../ui/toast.js';
import { renderLeft } from './rail-left.js';
import { effectsPanel, textGeometryRows, textSurfaceRows } from './inspector-text.js';
import { destroyTextAreaEditor, textAreaContentPanel, textAreaRows } from './inspector-textarea.js';
import { stopwatch } from './timeline-view.js';
import { movesPanel } from './moves-panel.js';
import { acceptFor, replaceSource } from '../scene/replace-source.js';

export let embedPhoto = true;

/* Mirrors the options in index.html's #tlFps. Here so the "match the project
   to this clip" button can tell whether the rate it wants is one the project
   can actually be set to. */
const TIMELINE_FPS = [24, 25, 30, 60];

/* Icon tabs above Object/Shape/Surface — recess, text and image are the
   only types where those three panels run consecutively with nothing else
   mixed in (frame/model stop before Surface exists at all; camera/light
   have no Shape/Surface split to switch between; textarea's Content panel
   sits between Object and Shape, breaking the run) — see renderObjectInspector.
   A tab doesn't hide anything the way real tab panels would: it opens its
   own <details> and closes the other two, reusing the same fold state
   every panel already tracks (see ui/controls.js's panel()), so manually
   opening more than one from their own headers still works exactly like
   any other foldable panel — the tabs are just a shortcut to one at a time. */
const SHAPE_ICON = '<rect x="2.4" y="2.4" width="11.2" height="11.2" rx="1" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M2.4 6.2h11.2M6.2 2.4v11.2" stroke="currentColor" stroke-width="1.3"/>';
const SURFACE_ICON = '<circle cx="8" cy="8" r="5.6" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8 2.4a5.6 5.6 0 0 1 0 11.2z" fill="currentColor" stroke="none" opacity=".35"/>';

/* RIGHT RAIL — the inspector.

   Panels are rebuilt wholesale rather than diffed. At this size that is cheaper
   than tracking what changed, and it cannot go stale. refreshInspectorValues is
   the exception: during a scrub the fields have to follow the drag without the
   DOM churn, so the number inputs are held in inspectorRefs and written directly. */

let inspectorRefs = [];
/* What renderRight last drew, so it can tell "the same thing, one field
   changed" from "a different selection entirely" — see the scroll-restore
   note below for why the difference matters. */
let lastRailKey = null;
export function renderRight(){
  if(scrubbing || isEditing()){ deferRail('R'); return; }
  const R = $('#railR');
  const focus = captureFocus(R);
  /* .rail is itself the scroller, and a wholesale rebuild resets it to 0 —
     so changing a setting halfway down the panel used to fling you back to
     the top. Restored AFTER restoreFocus, never before: focus() scrolls its
     element into view, which would otherwise undo this on the way past.

     Only when it's the SAME selection, though. This used to restore
     unconditionally, which was right for "one field changed, rebuild the
     same panel" and silently wrong for "a different selection replaced it
     outright" — scrolled deep into one object's Effects panel, click a
     shorter one (Scene Settings included, now that it's two panels instead
     of five), and the old offset landed past the new content's own end,
     often past the Scene list and the Scene Settings row themselves — no
     visible way back without already knowing to scroll up first. A real
     selection change reads as new content and starts at the top, same as
     opening any other panel does. */
  const key = S.stage + ':' + S.selId;
  const sameSelection = key === lastRailKey;
  lastRailKey = key;
  const scroll = sameSelection ? R.scrollTop : 0;
  try{ renderRightBody(R); } finally { restoreFocus(R, focus); R.scrollTop = scroll; }
}
/* Typing into a field that chains a rebuild into its own onChange (Cap height,
   Extrusion, a recess's auto-fit dimensions, Text Area's Width/Base size/
   Padding) used to lose focus the moment the DOM was torn down and rebuilt —
   see controls.js's captureFocus for why. Split out — with several early
   returns below (camera, light, model) — so the wrapper above restores focus
   however this body returns. */
function renderRightBody(R){
  /* Before the innerHTML reset below, not after: a Tiptap editor owns a
     ProseMirror view, plugins and document-level listeners that removing its
     element does not release. Selecting a different object (or any other
     rebuild) has to tear it down explicitly or every visit to a Text Area
     leaks another live instance. */
  destroyTextAreaEditor();
  R.innerHTML=''; inspectorRefs=[];

  // Calibrate is its own focused view now (#calibPanel, beside the photo —
  // see ui/rail-left.js) and the right dock is hidden entirely while it's
  // active (ui/chrome.js's goStage), so there's nothing to build here.
  if(S.stage==='calib') return;

  // The outliner sits right above the Inspector now — the scene list and
  // the properties of whatever it selects are one dock, not two rails you
  // look between. Shown in both Design and Export (selecting/hiding things
  // is just as meaningful while reviewing as while composing); only
  // Calibrate has no scene to list yet.
  /* Folded away by default in Motion, and only there. The timeline dock
     below is what you are working in at that point, and it takes the room
     the object list was using — the list is still one click away, and its
     open/closed state is remembered separately per stage so folding it here
     does not also fold it in Design. */
  const animStage = S.stage === 'animate';
  const sceneP = panel('Scene', S.objects.length+' object'+(S.objects.length===1?'':'s'),
    { key: animStage ? 'Scene/motion' : 'Scene', closed: animStage });
  if(!S.objects.length){
    sceneP.body.appendChild(el('div','empty')).innerHTML = '<b>Nothing in the scene yet</b>Add a shape from the tools on the left, then position it so it breaks the plane of the screen — drag in the viewport to move, hold <b>Shift</b> to push into depth.';
  }else{
    const list = el('div','objs');
    /* NESTED, NOT JUST MARKED — a linked object reads as belonging under its
       master, the way every other tool shows a rig, rather than sitting
       wherever it happens to fall in the flat list with only a small icon to
       explain why. This is purely how the list DISPLAYS: the underlying
       S.objects order it walks (newest first, same as ever) still governs
       layer/paint order and moveObject's reordering — see that function's own
       note on why list position and render depth are already two different
       things. A child is walked in that same relative order, immediately
       under its master, at any depth a chain of links happens to reach. */
    const reversed = S.objects.slice().reverse();
    const childrenOf = pid => reversed.filter(o => o.parentId === pid);
    const isRoot = o => o.parentId == null || !S.objects.some(p => p.id === o.parentId);
    const appendRow = (ob, depth) => {
      const row = el('div','objrow'+(ob.id===S.selId?' on':''));
      row.tabIndex = 0;
      if(depth > 0) row.style.paddingLeft = (8 + depth*16) + 'px';
      const parent = ob.parentId != null ? S.objects.find(p=>p.id===ob.parentId) : null;
      if(parent){
        const link = el('span','ico linkico');
        link.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M6.5 9.5 9.5 6.5"/><path d="M7 5 8.3 3.7a2.3 2.3 0 0 1 3.3 3.3L10 8.3"/><path d="M9 11 7.7 12.3a2.3 2.3 0 0 1-3.3-3.3L6 7.7"/></svg>';
        link.title = 'Linked to ' + parent.name;
        row.appendChild(link);
      }
      const sw = el('div','sw'); sw.style.background=ob.color;
      row.appendChild(sw);
      row.appendChild(el('span','nm',ob.name));
      row.appendChild(el('span','ty',ob.type));
      const v = el('button','vis'+(ob.visible?'':' off'));
      v.innerHTML = ob.visible?EYE_ON:EYE_OFF;
      v.title = ob.visible?'Hide':'Show';
      v.onclick = e=>{ e.stopPropagation(); ob.visible=!ob.visible; captureKey(ob,'visible'); syncTransform(ob); renderLeft(); renderRight(); requestRender(); pushHistory(); };
      row.appendChild(v);
      row.onclick = ()=>select(ob.id);
      row.onkeydown = e=>{ if(e.key==='Enter'||e.key===' '){e.preventDefault();select(ob.id);} };
      /* DRAG TO REORDER, OR DROP ON TO LINK. The list is the one that owns the
         layer order and the timeline follows it, because this list holds
         every object while the dock only shows the animated ones — an order
         expressed there could not say where the rest belong. Linking rides
         the same gesture rather than a second one: the top/bottom slivers of
         a row still mean "reorder around this", and the middle of the row —
         a real "drop ON it", not just near it — means "link to this".

         Native drag and drop rather than pointer events: this is a list of
         rows being reordered, which is the one thing HTML's own drag is good
         at, and it gives the grab cursor and the drag image for nothing. A
         drag does not fire click afterwards, so row.onclick above is safe. */
      row.draggable = true;
      row.dataset.objId = ob.id;
      row.ondragstart = e=>{
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(ob.id));
        row.classList.add('dragging');
      };
      row.ondragend = ()=>{
        row.classList.remove('dragging');
        list.querySelectorAll('.objrow').forEach(r=>r.classList.remove('dropabove','dropbelow','droplink'));
      };
      row.ondragover = e=>{
        e.preventDefault();                       // without this, drop never fires
        e.dataTransfer.dropEffect = 'move';
        const r = row.getBoundingClientRect();
        const frac = (e.clientY - r.top) / r.height;
        const zone = frac < .3 ? 'above' : frac > .7 ? 'below' : 'link';
        row.classList.toggle('dropabove', zone==='above');
        row.classList.toggle('dropbelow', zone==='below');
        row.classList.toggle('droplink', zone==='link');
      };
      row.ondragleave = ()=>row.classList.remove('dropabove','dropbelow','droplink');
      row.ondrop = e=>{
        e.preventDefault();
        const zone = row.classList.contains('dropbelow') ? 'below'
          : row.classList.contains('droplink') ? 'link' : 'above';
        row.classList.remove('dropabove','dropbelow','droplink');
        const dragId = +e.dataTransfer.getData('text/plain');
        if(!dragId) return;
        if(zone === 'link'){
          if(dragId === ob.id) return;
          if(wouldCreateCycle(dragId, ob.id)) toast('Can’t link — that would put an object inside itself.', true);
          else if(!linkObjects(dragId, ob.id)) toast('That can’t be linked.', true);
        }else moveObject(dragId, ob.id, zone==='below');
      };
      list.appendChild(row);
      childrenOf(ob.id).forEach(c => appendRow(c, depth+1));
    };
    reversed.filter(isRoot).forEach(ob => appendRow(ob, 0));
    if(sceneListH) list.style.maxHeight = sceneListH + 'px';
    sceneP.body.appendChild(list);
    sceneP.body.appendChild(sceneListGrip(list));
    const br = el('div','btnrow');
    const d1 = el('button','btn btn--sm','Duplicate');
    d1.disabled = !S.selId || getSel()?.type==='camera';
    if(d1.disabled && S.selId) d1.title = 'Only one camera is allowed — it is the viewer, not a prop.';
    d1.onclick = duplicateSel;
    const d2 = el('button','btn btn--sm btn--dgr','Remove'); d2.disabled=!S.selId; d2.onclick=deleteSel;
    br.appendChild(d1); br.appendChild(d2);
    sceneP.body.appendChild(br);
  }
  // Not a scene object — doesn't render, can't be hidden or deleted, so no
  // eye toggle. Everything that used to just tag along with whatever object
  // happened to be selected (Calibration, Light & stage, the view-specific
  // Zoom/Viewer panels) lives here instead, once, regardless of what else
  // is selected — see renderMasterSettings below. Named "Scene", not
  // "Master Settings": the panel it opens is sorted by ownership now, not a
  // grab-bag, and the bare word would have put a row called Scene inside
  // the very panel above it that is already called Scene — see the "s"
  // for what distinguishes the two.
  /* THE SCREEN ARRAY, in the list because that is where you look for a thing.
     Its settings began life in Scene Settings, under a heading about the
     installation, which is the correct place for them and an unfindable one —
     you have to already know an array is a property of the scene rather than a
     thing in it. It reads as a thing, so it is listed as one.

     Not in the left tool menu, though, and that is the distinction: those add
     content, and this is not content — it is the screen the content is for.
     There is no "add an LCD video wall" any more than there is "add a screen".
     It appears when a project has one and goes when it does. */
  if(S.screenArray){
    const arrRow = el('div','objrow master'+(S.arraySelected?' on':'')+(animStage?' dim':''));
    arrRow.tabIndex = animStage ? -1 : 0;
    // a grid of panels, which is what it is
    arrRow.innerHTML = '<svg class="ico" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3">'
      + '<rect x="1.5" y="3" width="5.6" height="4.4" rx=".6"/><rect x="8.9" y="3" width="5.6" height="4.4" rx=".6"/>'
      + '<rect x="1.5" y="8.6" width="5.6" height="4.4" rx=".6"/><rect x="8.9" y="8.6" width="5.6" height="4.4" rx=".6"/>'
      + '</svg><span class="nm">LCD Video Wall</span>'
      + '<span class="ty">' + (S.screenArray.mode === 'free'
          ? S.screenArray.screens.length + (S.screenArray.screens.length === 1 ? ' screen' : ' screens')
          : S.screenArray.cols + '×' + S.screenArray.rows) + '</span>';
    if(animStage){
      arrRow.title = 'The LCD video wall is edited in Design.';
    }else{
      const pick = ()=>{ select(null); S.arraySelected = true; renderRight(); renderLeft(); };
      arrRow.onclick = pick;
      arrRow.onkeydown = e=>{ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); pick(); } };
    }
    sceneP.body.appendChild(arrRow);
  }

  const msRow = el('div','objrow master'+(!S.selId && !S.arraySelected?' on':'')+(animStage?' dim':''));
  msRow.tabIndex = animStage ? -1 : 0;
  // A sliders/equalizer glyph rather than a gear — reads clearly at 16px
  // (eight thin spokes off a small circle blurred together at this size),
  // and "adjustable rows" fits a settings PANEL better than "a mechanism."
  msRow.innerHTML = '<svg class="ico" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M2 4.5h12M2 8h12M2 11.5h12"/><circle cx="6" cy="4.5" r="1.4" fill="currentColor" stroke="none"/><circle cx="10.5" cy="8" r="1.4" fill="currentColor" stroke="none"/><circle cx="7.5" cy="11.5" r="1.4" fill="currentColor" stroke="none"/></svg><span class="nm">Scene Settings</span>';
  if(animStage){
    // Lighting and background are Design decisions, not timing ones, so
    // Motion hands the whole stage over rather than rendering them here —
    // see renderAnimateInspector's own empty state. Left clickable-looking
    // but inert would answer a question nobody asked; dimmed and titled
    // says so instead of pretending there's something behind it.
    msRow.title = 'Scene settings are edited in Design.';
  }else{
    msRow.onclick = ()=>select(null);
    msRow.onkeydown = e=>{ if(e.key==='Enter'||e.key===' '){e.preventDefault();select(null);} };
  }
  sceneP.body.appendChild(msRow);
  R.appendChild(sceneP);

  const o = getSel();
  if(S.stage === 'animate') renderAnimateInspector(R, o);
  else if(S.arraySelected && S.screenArray) renderArrayInspector(R);
  else if(!o) renderMasterSettings(R);
  else renderObjectInspector(R, o);
}

/* THE SCENE LIST'S HEIGHT, dragged. It is capped (.objs, app.css) so a long
   scene does not push the inspector off the rail — but a scene with a dozen
   objects and their nulls wants more room than that cap, and only the person
   looking at it knows how much. A grip under the list drags the cap; the
   height is theirs, kept for this browser like the timeline's, and a
   double-click on the grip puts the default back. */
const SCENE_H_KEY = 'illusio3d.scene-list-height';
let sceneListH = null;
try { sceneListH = +localStorage.getItem(SCENE_H_KEY) || null; } catch (_) { /* private mode */ }
function sceneListGrip(list){
  const grip = el('div','objs__grip');
  grip.title = 'Drag to make the list taller or shorter · double-click to reset';
  grip.addEventListener('pointerdown', ev=>{
    if(ev.button !== 0) return;
    ev.preventDefault();
    grip.setPointerCapture(ev.pointerId);
    const y0 = ev.clientY, h0 = list.getBoundingClientRect().height;
    const move = e=>{
      sceneListH = Math.round(Math.max(72, Math.min(window.innerHeight * .8, h0 + e.clientY - y0)));
      list.style.maxHeight = sceneListH + 'px';
    };
    const up = ()=>{
      grip.removeEventListener('pointermove', move); grip.removeEventListener('pointerup', up);
      try { localStorage.setItem(SCENE_H_KEY, String(sceneListH)); } catch (_) {}
    };
    grip.addEventListener('pointermove', move); grip.addEventListener('pointerup', up);
  });
  grip.addEventListener('dblclick', ()=>{
    sceneListH = null; list.style.maxHeight = '';
    try { localStorage.removeItem(SCENE_H_KEY); } catch (_) {}
  });
  return grip;
}

/* THE SCREEN ARRAY'S INSPECTOR.

   Selected from the outliner like an object, and edited here like one, though
   it is not one — see store.js's arraySelected for why making it a real scene
   object would have been the expensive way to get a row in a list.

   Split into tabs on the same reasoning the object inspector is: two questions,
   not one list. GRID is how many screens and how far apart — the shape of the
   wall. SCREEN is what one display actually is, the two numbers you would read
   off a product page. Mixing them meant six fields where four of them never
   change once the hardware is chosen. */
/* GRID converted to FREE, screen by screen, at the exact positions the grid
 * already put them — so turning the toggle on loses nothing: the drawing
 * does not move, only what can be done to it changes. Top-left corners, in
 * the same units the grid itself walks in (see panels.js's freeBounds for
 * why that convention was chosen). */
function gridToFree(a){
  const screens = [];
  for(let r = 0; r < a.rows; r++)
    for(let c = 0; c < a.cols; c++)
      screens.push({
        key: String.fromCharCode(65 + (r * a.cols + c) % 26),
        w: a.tile.w, h: a.tile.h,
        x: c * (a.tile.w + a.gap), y: r * (a.tile.h + a.gap),
        resW: a.res.w, resH: a.res.h
      });
  return { mode: 'free', screens };
}

/** The next unused letter, so a new or converted screen never collides with
 *  one already on the wall. */
function nextFreeKey(screens){
  const used = new Set(screens.map(s => s.key));
  for(let c = 65; c < 65 + 26; c++) if(!used.has(String.fromCharCode(c))) return String.fromCharCode(c);
  return 'S' + (screens.length + 1);        // past Z — vanishingly rare, still has to return something
}

/* ONE PANE PER SCREEN — three pairRow's rather than six numRow's, because a
   screen's size, its position and its resolution are each two numbers that
   are one measurement (see controls.js's own note on pairRow), the same
   reasoning that already puts a corner panel's W×H on one line. Keyed by
   INDEX rather than by the screen's own label, because the label is itself
   one of the editable fields — keying off a value that can change under the
   key would be the bug, not a convenience. */
function renderFreeEditor(p, arr, upd){
  const list = el('div','pane');
  list.appendChild(el('p','note',
    'Each screen keeps its own size, resolution and position. X/Y is its top-left corner, in metres, measured from wherever you like — only the differences between screens matter, since the wall is fitted to whatever they add up to. There is no one margin to set, so everything that is not a screen is shown as the wall behind them — turn it off, or down, below.'));

  arr.screens.forEach((s, i) => {
    if(i > 0) list.appendChild(el('div','rule'));
    const head = el('div','f');
    head.appendChild(el('label',null,'Label'));
    const keyIn = el('input','inp');
    keyIn.type = 'text'; keyIn.value = s.key; keyIn.maxLength = 12;
    keyIn.title = 'Names this screen in the exported files and the README.';
    keyIn.dataset.field = 'arrFreeKey' + i;
    keyIn.onchange = () => {
      const v = keyIn.value.trim().slice(0,12);
      if(!v){ keyIn.value = s.key; return; }
      if(arr.screens.some((o,j) => j !== i && o.key === v)){
        toast(`Screen ${v} already exists — labels have to be unique.`, true);
        keyIn.value = s.key; return;
      }
      s.key = v; upd();
    };
    head.appendChild(keyIn);
    if(arr.screens.length > 1){
      const rm = el('button','btn btn--sm btn--gh','Remove');
      rm.title = `Remove screen ${s.key}`;
      rm.onclick = () => { arr.screens.splice(i,1); upd(); };
      head.appendChild(rm);
    }
    list.appendChild(head);

    /* PIXELS ARE THE SOURCE OF TRUTH FOR ASPECT, exactly as they are for a
       grid's own "Screen" tab and the single-screen fields in rail-left.js —
       "the pitch is what ties its pixels to its metres, and a display whose
       two disagree is a display nobody makes." Editing Size holds the pixel
       aspect fixed and derives the OTHER metre dimension to match it; editing
       Resolution updates the pitch and re-derives the metre height off the
       (now new) aspect, width held fixed — the same asymmetry grid mode's own
       four fields already have, kept rather than smoothed over so a habit
       formed on one screen carries to the other. */
    const aspect = () => s.resW / Math.max(1, s.resH);
    list.appendChild(pairRow('Size', [
      { value: s.w, step: .01, onChange: v => { s.w = Math.max(.02, v); s.h = s.w/aspect(); upd(); }, key: 'arrFreeW'+i },
      { value: s.h, step: .01, onChange: v => { s.h = Math.max(.02, v); s.w = s.h*aspect(); upd(); }, key: 'arrFreeH'+i }
    ], 'm'));
    list.appendChild(pairRow('Position', [
      { value: s.x, step: .01, onChange: v => { s.x = v; upd(); }, key: 'arrFreeX'+i },
      { value: s.y, step: .01, onChange: v => { s.y = v; upd(); }, key: 'arrFreeY'+i }
    ], 'm'));
    list.appendChild(pairRow('Resolution', [
      { value: s.resW, step: 1, onChange: v => { s.resW = Math.max(1, Math.round(v)); s.h = s.w/aspect(); upd(); }, key: 'arrFreeResW'+i },
      { value: s.resH, step: 1, onChange: v => { s.resH = Math.max(1, Math.round(v)); s.h = s.w/aspect(); upd(); }, key: 'arrFreeResH'+i }
    ], 'px'));
  });

  const add = el('button','btn btn--sm btn--gh btn--w','Add a screen');
  add.onclick = () => {
    if(arr.screens.length >= MAX_TILES){
      toast(`${MAX_TILES} screens is the most one wall can export at once.`, true);
      return;
    }
    /* Beside the rightmost edge of whatever is already there, at the last
       screen's own size and resolution — a plausible next screen rather than
       a default shape landing on top of an existing one. */
    const last = arr.screens[arr.screens.length - 1];
    const rightEdge = Math.max(...arr.screens.map(s => s.x + s.w));
    arr.screens.push({ key: nextFreeKey(arr.screens), w: last.w, h: last.h,
                        x: rightEdge, y: last.y, resW: last.resW, resH: last.resH });
    upd();
  };
  list.appendChild(add);
  p.body.appendChild(list);
}

export function renderArrayInspector(R){
  const arr = S.screenArray;
  const free = arr.mode === 'free';
  const p = panel('LCD Video Wall',
    free ? arr.screens.length + (arr.screens.length === 1 ? ' screen' : ' screens') : arr.cols + ' × ' + arr.rows,
    { key: 'ScreenArray' });

  /* The drawing first, because it answers "what am I building" before any of the
     numbers do. createElementNS, not store.js's el(): that calls createElement,
     which puts <svg> in the HTML namespace — it lays out, reports a real size
     and answers querySelector like the real thing, while rendering nothing. */
  const planBox = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  planBox.setAttribute('class', 'arrplan');
  planBox.setAttribute('viewBox', '0 0 440 280');
  planBox.setAttribute('role', 'img');
  planBox.setAttribute('aria-label', 'Scale drawing of the LCD video wall with its dimensions');
  p.body.appendChild(planBox);
  const redrawPlan = () => { if(planBox.isConnected) drawPlan(planBox, false); };
  requestAnimationFrame(redrawPlan);

  /* Committing through onScreenChange is what makes every field here reach the
     scene: syncArrayWall rewrites S.screen/S.res from the grid, and the refit
     that follows re-fits the one inset across the new wall.

     redrawPlan runs EVERY time, including mid-drag — renderRight() does not:
     it defers itself while scrubbing (see controls.js's own note on
     scrubbing/flushRails), on purpose, because rebuilding the whole rail
     mid-drag would destroy the very field being dragged. That guard is
     right for the rail and wrong for the plan — this drawing has no field
     inside it to destroy, it is what someone dragging a screen's position is
     actually watching, and a drawing that only catches up once the mouse
     lets go is not the live feedback the drag exists to give. drawPlan
     itself is cheap (pure function, reads state, writes SVG — see
     plan-view.js), so redrawing it every tick costs nothing the deferred
     rebuild was trying to save. */
  const upd = () => { onScreenChange(); redrawPlan(); renderRight(); renderLeft(); };

  /* GRID vs FREE. A grid wall is many identical displays at one pitch, laid
     out by formula — two numbers say the whole shape. Free places each
     screen by hand: different sizes, offset however they actually hang,
     still all in the one plane the eye is calibrated against. Turning this
     ON keeps the layout exactly as it was — see gridToFree — so nothing is
     lost by trying it. Turning it back OFF has no such translation: an
     arbitrary free layout is not, in general, a grid, so it starts over. */
  const modeRow = el('div','pane');
  modeRow.appendChild(segRow('Layout', free ? 'free' : 'grid',
    [['grid','Grid'],['free','Free placement']], v => {
      if(v === 'free' && !free) S.screenArray = gridToFree(arr);
      else if(v === 'grid' && free){
        S.screenArray = { cols: 2, rows: 1, gap: .02, tile: { w: 1.21, h: .68 }, res: { w: 1920, h: 1080 } };
        toast('A free layout has no equivalent grid, so this starts over with a fresh two-screen wall.');
      }
      upd();
    }, { key: 'arrMode' }));
  p.body.appendChild(modeRow);

  if(free){
    renderFreeEditor(p, arr, upd);
  }else{

  const grid = el('div','pane');
  /* Capped where it can still be explained. The export opens one encoder per
     screen and holds every finished file in memory until the ZIP is written, so
     this is a real limit rather than a tidy one — see doc.js's MAX_TILES. */
  const setGrid = (key, v) => {
    const next = Math.max(1, Math.round(v));
    const other = key === 'cols' ? arr.rows : arr.cols;
    if(next * other > MAX_TILES){
      toast(`${MAX_TILES} screens is the most one wall can export at once.`, true);
      renderRight(); return;
    }
    arr[key] = next; upd();
  };
  grid.appendChild(numRow('Screens across', arr.cols, 1, v=>setGrid('cols', v), '', 'arrCols'));
  grid.appendChild(numRow('Screens down',   arr.rows, 1, v=>setGrid('rows', v), '', 'arrRows'));
  grid.appendChild(numRow('Margin', arr.gap, .005, v=>{ arr.gap = Math.max(0, v); upd(); }, 'm', 'arrGap'));
  grid.appendChild(el('p','note','The margin is the physical gap between displays. The scene continues behind it: each screen shows the true window for where it sits, so the strip a bezel covers is in none of the files.'));

  const one = el('div','pane');
  /* One screen's own aspect, locked the way the single-screen rows are: the
     pitch is what ties its pixels to its metres, and a display whose two
     disagree is a display nobody makes. */
  const tileAspect = () => arr.res.w / Math.max(1, arr.res.h);
  one.appendChild(numRow('Pixels across', arr.res.w, 1, v=>{
    arr.res.w = Math.max(1, Math.round(v)); arr.tile.h = arr.tile.w/tileAspect(); upd();
  }, 'px', 'arrResW'));
  one.appendChild(numRow('Pixels down', arr.res.h, 1, v=>{
    arr.res.h = Math.max(1, Math.round(v)); arr.tile.h = arr.tile.w/tileAspect(); upd();
  }, 'px', 'arrResH'));
  one.appendChild(numRow('Width', arr.tile.w, .01, v=>{
    arr.tile.w = Math.max(.02, v); arr.tile.h = arr.tile.w/tileAspect(); upd();
  }, 'm', 'arrTileW'));
  one.appendChild(numRow('Height', arr.tile.h, .01, v=>{
    arr.tile.h = Math.max(.02, v); arr.tile.w = arr.tile.h*tileAspect(); upd();
  }, 'm', 'arrTileH'));

  p.body.appendChild(tabStrip([['Grid', grid], ['Screen', one]], { key: 'ScreenArray/tabs' }));
  p.body.appendChild(grid); p.body.appendChild(one);
  }

  /* HOW MUCH OF THE MARGIN YOU WANT TO LOOK AT — a different question from how
     wide it is, and the one question that still has an answer even where the
     width does not: free placement has no single number to set, but every
     screen still gets its own bezel frame (scene/helpers.js), and these three
     say how loud it is. Own block rather than living inside the Grid tab, so
     switching to Screen does not also hide it — it applies to both, and now
     to both modes. Drawn black and over everything, a real wall's bezels are
     also the most visually dominant thing on screen while judging the
     artwork behind them — so they turn down, or off. Neither control can
     reach the deliverable: every screen is exported through a frustum that is
     exactly its own rectangle, so a bezel is outside every file whatever it
     is doing here. */
  const marginBlock = el('div','pane');
  const showB = S.showBezels !== false;
  marginBlock.appendChild(checkRow('Show the margins', showB, v=>{
    S.showBezels = v; rebuildHelpers(); requestRender(); renderRight();
  }));
  if(showB){
    /* The third thing a margin can be. Offered only where it means something:
       On-site is the only view with a photograph behind the render, so it is
       the only one where a bezel can show the building rather than stand in
       front of it. Said plainly rather than hidden when the view is wrong — a
       control that appears and disappears as you switch tabs is harder to find
       again than one that explains itself. */
    const photo = S.bezelPhoto === true;
    marginBlock.appendChild(checkRow('Show the photo through the margins', photo, v=>{
      S.bezelPhoto = v; requestRender(); renderRight();
    }));
    marginBlock.appendChild(el('p','note', photo
      ? 'On-site, the margins are cut straight through to the photograph, so the wall sits in the building instead of on top of it. Orbit and Flat keep the black bars — there is no photograph behind them.'
      : (S.photo ? 'Black bars, as an unlit bezel looks. Turn this on to cut them through to the reference photo in the On-site view.'
                 : 'Black bars, as an unlit bezel looks. With a reference photo loaded they can instead be cut through to it in the On-site view.')));
    if(!photo)
      marginBlock.appendChild(sliderRow('Margin opacity', S.bezelOpacity ?? 1, 0, 1, .05, v=>{
        S.bezelOpacity = v; rebuildHelpers(); requestRender();
      }));
  }
  p.body.appendChild(marginBlock);

  const wall = wallOf(arr);
  if(free){
    const n = arr.screens.length;
    p.body.appendChild(readout([
      ['Wall', fmt(wall.w,2) + ' × ' + fmt(wall.h,2) + ' m overall'],
      /* Sizes named individually rather than "each", since a free wall's
         whole point is that they need not match. */
      ['Export', n + (n === 1 ? ' file' : ' files') + ': ' + arr.screens.map(s => s.resW + '×' + s.resH).join(', ')]
    ]));
  }else{
    const n = arr.cols * arr.rows;
    p.body.appendChild(readout([
      ['Wall', fmt(wall.w,2) + ' × ' + fmt(wall.h,2) + ' m'],
      ['Export', n + (n === 1 ? ' file' : ' files') + ', ' + arr.res.w + ' × ' + arr.res.h + ' px each']
    ]));
  }
  /* AN OPTIONAL SITE PHOTO, for showing someone the wall on the building.

     Optional in the real sense: an array is complete without one, nothing is
     gated behind it and no export needs it. Added to a wall whose viewer was
     set by hand, the four marks are placement only — see
     ui/calibrate-view.js's placementOnly(); a wall that STARTED from a photo is
     calibrated by it like any other screen. */
  p.body.appendChild(el('div','rule'));
  p.body.appendChild(el('div','group__hd','Site mockup'));
  if(!S.photo){
    const add = el('button','btn btn--sm btn--gh btn--w','Add a site photo…');
    /* goStage FIRST, same as "Re-mark the corners…" below already does. This
       button lives in Design's own rail, and applyPhoto (calibrate-view.js)
       never switches stage itself — every OTHER way to load a photo starts
       on Calibrate already, so it never had to. Without this, the file
       picker, the crop dialog and the crosshairs all did their jobs
       correctly and invisibly: the corner-marking UI they built lives under
       #viewCalib, which Design's own .hide class was still covering. */
    add.onclick = ()=>{ goStage('calib'); $('#filePhoto').click(); };
    p.body.appendChild(add);
    p.body.appendChild(el('p','note','Optional. Mark the four corners of the whole wall on a photograph of the site and On-site will show the wall standing there. It places the mockup only — the wall, the viewer and every exported file are untouched.'));
  }else{
    const mark = el('button','btn btn--sm btn--gh btn--w','Re-mark the corners…');
    mark.onclick = ()=>goStage('calib');
    p.body.appendChild(mark);
    const drop = el('button','btn btn--sm btn--gh btn--w','Remove the site photo');
    drop.onclick = ()=>{
      /* Leaving On-site first: it is the one view that cannot exist without a
         photo, and dropping one while standing in it would leave an empty
         pane rather than a sensible place to be. */
      S.photo = null;
      /* setView either way, not only to escape the standalone view: Split's
         right pane goes back to being Flat when the photograph goes, which
         means #siteWrap has to go home and #fitboxSplitR has to come back —
         and re-homing both is exactly what setView does. */
      setView(S.view === 'site' ? 'orbit' : S.view);
      renderRight(); renderLeft(); requestRender();
    };
    p.body.appendChild(drop);
    p.body.appendChild(el('p','note', placementOnly()
      ? 'Placement only — the viewer was set by hand, so these marks say where the wall sits in the photograph and change nothing about the wall or the files it exports.'
      : 'The marks calibrate the viewer against this wall’s size. Change the wall and lock the angle again to keep them in step.'));
  }

  /* An EXISTING array stays editable without a licence, and says so instead.
     The gate belongs on making one and on shipping one; taking the controls
     away from a project that already has a wall would strand it — the numbers
     would still be in the file, driving the render, with nothing to change them
     by. What is actually withheld is the export, so that is what this names. */
  const gate = canUseScreenArray();
  if(!gate.ok){
    p.body.appendChild(el('p','note note--warn', gate.reason));
    const ask = el('button','btn btn--sm btn--w btn--pri', MOTION_CTA);
    ask.onclick = ()=>showTrial({ from: 'licence' });
    p.body.appendChild(ask);
  }

  const rm = el('button','btn btn--sm btn--gh btn--w','Back to a single screen');
  rm.onclick = ()=>{
    /* The wall's own size is kept, not reset: it is what every object on the
       scene was placed against, and snapping back to a default screen would
       move all of them to make a point about panels. */
    S.screenArray = null; S.arraySelected = false;
    onScreenChange(); renderRight(); renderLeft(); pushHistory();
  };
  p.body.appendChild(rm);
  R.appendChild(p);
}

/* THE MOTION INSPECTOR — the seven properties that can be keyframed, and
   nothing else.

   Not a filtered view of the full inspector: it is a different question. In
   Design you are asking what this object should look like, and forty controls
   is the right answer. Here you are asking what should change over time, and
   showing the other thirty-three would bury the seven that can. Every row is
   the same control the Design inspector uses, so a value edited here behaves
   identically — including writing a keyframe at the playhead when the
   property is animated (see ui/controls.js and state/timeline.js). */
/* REPLACE THE FILE, keeping the layer — a better shot, a re-cut, a client's
   revision. Deleting and re-adding loses the placement, the size, the mask,
   the effects and every keyframe, which is the expensive half.

   One input serves both types and its `accept` is set per click. The handler
   is rebound on every click so it always closes over the layer whose button
   was pressed: the rail is rebuilt wholesale, so a binding left from last time
   would swap the file on whatever happens to be selected now. */
function swapFileRow(o){
  const row = el('div','btnrow');
  const shape = o.type === 'shape';
  const b = el('button','btn btn--sm btn--gh btn--w', shape ? 'Replace with a 3D model file…' : 'Replace file…');
  b.title = shape
    ? 'Swap this shape for a .glb model at the same size, keeping its placement, name and keyframes.'
    : 'Swap the ' + (o.type === 'video' ? 'clip' : o.type === 'model' ? 'model' : 'picture')
      + ' for another, keeping this layer’s size, placement, effects and keyframes.';
  b.onclick = ()=>{
    const inp = $('#fileSwap');
    if(!inp) return;
    inp.accept = acceptFor(o);
    inp.onchange = async e=>{
      const f = e.target.files[0];
      e.target.value = '';            // so the same file can be picked twice
      inp.onchange = null;
      if(!f) return;
      b.disabled = true;
      try{
        await replaceSource(o, f);
        renderRight(); renderLeft();
        toast('Replaced with ' + f.name + '.');
      }catch(err){
        toast(err.message || 'That file could not be used.', true);
      }finally{ b.disabled = false; }
    };
    inp.click();
  };
  row.appendChild(b);
  return row;
}

/* animatableProps moved to core/anim.js when the move library needed the same
   answer — it is the authority on what an object may animate, and a second
   copy is how two features quietly stop agreeing. */

function renderAnimateInspector(R, o){
  if(!o){
    const p = panel('Motion', S.objects.length ? 'nothing selected' : 'empty scene');
    p.body.appendChild(el('div','empty')).innerHTML = S.objects.length
      ? '<b>Select something to animate</b>Pick an object from the Scene list above. Its keyframeable properties appear here, and its keyframes in the timeline below.'
      : '<b>Nothing to animate yet</b>Go back to <b>Design</b> and add something to the scene first.';
    R.appendChild(p);
    return;
  }

  const p1 = panel(NICE[o.type], o.name);
  /* Named, because only the edited track may be keyframed - see timeline.js.
     Position, Rotation and Scale each pass their own. */
  const upd = prop =>{ captureKey(o, prop); syncTransform(o); requestRender(); };
  const look = ()=>{ applyLook(o); captureKey(o,'opacity'); captureKey(o,'color'); captureKey(o,'intensity'); renderLeft(); requestRender(); };

  for(const prop of animatableProps(o)){
    if(prop === 'pos')   p1.body.appendChild(stopwatch(vec3Row('Position', o.pos, ['x','y','z'], .05, ()=>upd('pos'), inspectorRefs), o, 'pos'));
    if(prop === 'rot')   p1.body.appendChild(stopwatch(vec3Row('Rotation', o.rot, ['x','y','z'], 1, ()=>upd('rot'), inspectorRefs), o, 'rot'));
    if(prop === 'scale') p1.body.appendChild(stopwatch(vec3Row('Scale', o.scale, ['x','y','z'], .01, k=>{
      if(o.lockScale && k){ const v=o.scale[k]; o.scale.x=o.scale.y=o.scale.z=v; refreshInspectorValues(); }
      upd('scale');
    }, inspectorRefs), o, 'scale'));
    if(prop === 'intensity') p1.body.appendChild(stopwatch(numRow('Intensity', o.params.intensity,
      (o.params.kind==='directional'||o.params.kind==='ambient') ? .1 : 25,
      v=>{ o.params.intensity = Math.max(0,v); captureKey(o,'intensity'); applyLook(o); requestRender(); },
      (o.params.kind==='directional'||o.params.kind==='ambient') ? 'lx' : 'cd'), o, 'intensity'));
    if(prop === 'color') p1.body.appendChild(stopwatch(colorRow('Colour', o.color, v=>{ o.color=v; look(); }), o, 'color'));
    if(prop === 'opacity') p1.body.appendChild(stopwatch(sliderRow('Opacity', o.opacity, 0,1,.01, v=>{ o.opacity=v; look(); }), o, 'opacity'));
    if(prop === 'visible') p1.body.appendChild(stopwatch(checkRow('Visible', o.visible, v=>{
      o.visible=v; captureKey(o,'visible'); syncTransform(o); renderLeft(); requestRender();
    }), o, 'visible'));
    /* A slider rather than a number: it is a proportion of the block, and
       dragging it scrubs the words in and out, which is how you set the two
       ends of the reveal by eye. Repaints the bitmap rather than rebuilding
       the object — see applyReveal. */
    if(prop === 'reveal') p1.body.appendChild(stopwatch(sliderRow('Reveal', revealOf(o), 0,1,.01, v=>{
      o.params.reveal=v; captureKey(o,'reveal'); applyReveal(o); requestRender();
    }), o, 'reveal'));
    /* The trailing edge of the same wipe. Directly under Reveal because the
       two are one control in two halves — how much has arrived, how much has
       gone — and reading them apart is how the three exits become obvious. */
    if(prop === 'erase') p1.body.appendChild(stopwatch(sliderRow('Erase', eraseOf(o), 0,1,.01, v=>{
      o.params.erase=v; captureKey(o,'erase'); applyReveal(o); requestRender();
    }), o, 'erase'));
  }
  R.appendChild(p1);

  /* Between the properties and the explanation. By this point the rows above
     have said what this object can animate; the panel offers to do it. */
  const pm = movesPanel(o);
  if (pm) R.appendChild(pm);

  const p2 = panel('How this works');
  p2.body.appendChild(el('p','note','Click a stopwatch to start animating that property — it drops a keyframe at the playhead. Move the playhead, change the value, and the next keyframe writes itself.'));
  p2.body.appendChild(el('p','note','In the timeline: drag the ruler to scrub, drag a diamond to retime it, right-click one for easing or to delete it. Space plays and pauses.'));
  R.appendChild(p2);
}

/* ── the pivot picker ──────────────────────────────────────────────────────
   Where rotation and scale happen from, picked by pointing at it rather
   than typed as three numbers: a 3×3 grid for the face you are looking at,
   and front/middle/back for depth. Twenty-seven positions out of twelve
   controls, and the shape of the widget says what it does without a legend.

   Bottom centre is the one that earns this feature — an object that leans
   out of the bezel has to hinge at its base, and around the middle it
   sinks half of itself through whatever it is standing on instead.

   Coordinates are normalised to the bounding box (see scene/objects.js), so
   a choice survives resizing: "bottom" stays the bottom when the box gets
   taller, and the object then grows upward out of a fixed base. */

const PIVOT_ROWS = [1, 0, -1];      // top → bottom, matching +Y up
const PIVOT_COLS = [-1, 0, 1];      // left → right
const PIVOT_DEPTHS = [['Front', 1], ['Middle', 0], ['Back', -1]];

const pivotName = (x, y) => {
  const v = y > 0 ? 'Top' : y < 0 ? 'Bottom' : '';
  const h = x < 0 ? 'left' : x > 0 ? 'right' : 'centre';
  return v ? `${v} ${h}` : h[0].toUpperCase() + h.slice(1);
};

function pivotWidget(o, after){
  const wrap = el('div', 'pv');
  const cur = o.pivot;

  const grid = el('div', 'pv__grid');
  for(const y of PIVOT_ROWS) for(const x of PIVOT_COLS){
    const b = el('button', 'pv__cell' + (cur && cur.x === x && cur.y === y ? ' on' : ''));
    b.title = pivotName(x, y);
    /* Depth is left as it was — picking a corner on the face should not
       silently reset how deep the pivot sits. Nothing chosen yet means the
       middle, which is the one that surprises nobody. */
    b.onclick = () => { setPivot(o, { x, y, z: cur ? cur.z : 0 }); after(); };
    grid.appendChild(b);
  }
  wrap.appendChild(grid);

  const side = el('div', 'pv__side');
  const seg = el('div', 'pv__depth');
  for(const [label, z] of PIVOT_DEPTHS){
    const b = el('button', 'pv__seg' + (cur && cur.z === z ? ' on' : ''), label);
    b.title = z > 0 ? 'Toward the viewer' : z < 0 ? 'Away from the viewer' : 'Halfway through';
    b.onclick = () => { setPivot(o, { x: cur ? cur.x : 0, y: cur ? cur.y : 0, z }); after(); };
    seg.appendChild(b);
  }
  side.appendChild(seg);

  /* Not the same as picking the middle: unset means the geometry's own
     origin, which for 3D Text is its baseline rather than the centre of its
     bounding box. Without a way back, a project could never be returned to
     exactly the state it opened in. */
  const reset = el('button', 'pv__reset', 'Default');
  reset.title = 'Back to the object’s own origin';
  reset.disabled = !cur;
  reset.onclick = () => { setPivot(o, null); after(); };
  side.appendChild(reset);
  wrap.appendChild(side);

  const row = stackRow('Pivot', wrap);
  row.appendChild(el('p', 'note', cur
    ? `Rotation and scale happen from the ${pivotName(cur.x, cur.y).toLowerCase()} of the ` +
      `${PIVOT_DEPTHS.find(d => d[1] === cur.z)[0].toLowerCase()} face. Moving it doesn’t move the object.`
    : 'Rotation and scale happen from the object’s own origin. Pick a corner or an edge to hinge from there instead — useful for anything that should lean or grow out of its base.'));
  return row;
}

/* Collapsed to one row until touched — the full grid above is a lot of
   panel (nine dots, three buttons, a reset) for something touched once a
   project. `after` already triggers a full renderRight() on every pick, so
   the widget rebuilds — and re-reads `o.pivot` — on every change; expanding
   it whenever a non-default pivot is already set is what keeps a choice
   you made from hiding the moment you make it. */
function pivotSummary(o, after){
  const cur = o.pivot;
  const nameFor = c => c
    ? `${pivotName(c.x, c.y)} · ${PIVOT_DEPTHS.find(d => d[1] === c.z)[0]}`
    : 'Centre';
  const wrap = el('div');
  // fieldRow, not a bare button carrying its own label text — an ordinary
  // .f row is what puts this in the same label column and left margin as
  // every row around it; a button reading "Hinge from Centre" end to end
  // read as a stray full-width bar instead of a field.
  const btn = el('button','btn btn--sm btn--w pvhinge', nameFor(cur));
  const full = pivotWidget(o, after);
  // classList, not the native hidden property — pivotWidget's own wrapper
  // is a .v3 (display:flex), and that author rule beats the user-agent
  // stylesheet's [hidden]{display:none} at equal specificity. See the
  // identical note on tabStrip's sync() in ui/controls.js.
  full.classList.toggle('hidden', !cur);
  btn.onclick = ()=>{ full.classList.toggle('hidden'); };
  const hingeRow = fieldRow('Hinge from', btn);
  hingeRow.querySelector('label').classList.add('rowicon');   // see the note on Scale axes above
  hingeRow.classList.add('rowunit');   // matches the grid's own value-column width, both edges
  wrap.appendChild(hingeRow);
  wrap.appendChild(full);
  return wrap;
}

/* The several early returns below (camera, light, model each stop once
   their own panels are done — there's no shared Shape/Surface tail for
   them) used to also skip past the Deliverable panels below when nothing
   was selected. Splitting this out means renderRightBody always reaches
   its own end regardless of which of these returns fires. */
function renderObjectInspector(R, o){
  /* Same reason as the gizmo's own path (ui/interaction.js): typing into
     Position while that property is animated has to write a keyframe at the
     playhead, or the next evaluation discards what was typed. */
  const upd = prop =>{ captureKey(o, prop); syncTransform(o); requestRender(); };
  const rebuild = ()=>{ syncNode(o); requestRender(); };
  const remat = ()=>{
    const m0 = o.node && o.node.userData.mats && o.node.userData.mats[0];
    // unlit swaps the material class, so a patch-in-place would be wrong
    const classMismatch = !m0 || !!o.unlit !== !!m0.isMeshBasicMaterial;
    /* Text always rebuilds. It used to also compare o.sideOn against the material
       count to spot a stale extrude material; that test cannot work now that
       userData.mats also carries the outline and effect materials, and it was
       already dead code — the o.type==='text' branch below catches every case. */
    if(o.type==='text'||o.type==='image'||o.type==='video'||classMismatch){ rebuild(); }
    /* The patch case is applyLook (scene/objects.js), shared with the
       animation player rather than written twice. It also fixed a real bug
       this had: it patched mats[0] alone, so an object with more than one
       material — a 3D Text object's outline and sides, a light's cone —
       changed opacity on only part of itself. */
    else applyLook(o);
    captureKey(o, 'opacity'); captureKey(o, 'color');
    renderLeft(); requestRender();
  };

  /* A frame is a fixed bezel, not a prop: it always sits centred on the screen,
     flat, unscaled, a hair in front of the recess. The only thing about it that
     is ever a choice is how wide its border is — see Form below — so
     Place (and the gizmo/drag/nudge paths that would otherwise move,
     rotate or scale it) is skipped entirely rather than shown and ignored.
     A recess is the same kind of fixed fixture, not a prop either — its
     opening/border/depth/bevel in Form already fully describe it,
     and it is always meant to sit centred on the screen the way autoFill
     assumes, so it gets the identical treatment: neither gets a Place tab
     at all, there being nothing left in it once Name has moved to the
     header below. */
  const fixedTransform = o.type === 'frame' || o.type === 'recess';

  // ── identity, always visible — the fix for "which object am I even
  // editing" once a long panel has scrolled the name field away ──────────
  const head = el('div','objhead');
  const ico = el('span','ico');
  ico.innerHTML = `<svg viewBox="0 0 16 16">${ICONS[o.type]||''}</svg>`;
  head.appendChild(ico);
  const ni = el('input','inp'); ni.value = o.name; ni.title = 'Name';
  ni.addEventListener('input',()=>{o.name=ni.value; renderLeft();}); histOn(ni);
  head.appendChild(ni);
  if(o.color){ const sw = el('span','sw'); sw.style.background = o.color; head.appendChild(sw); }
  const eyeBtn = el('button','eye');
  eyeBtn.innerHTML = o.visible ? EYE_ON : EYE_OFF;
  eyeBtn.title = o.visible ? 'Hide' : 'Show';
  eyeBtn.onclick = e=>{
    e.stopPropagation();
    o.visible=!o.visible; captureKey(o,'visible'); syncTransform(o); renderLeft(); renderRight(); requestRender(); pushHistory();
  };
  head.appendChild(eyeBtn);
  R.appendChild(head);

  const P = o.params;

  // ── Place — where it is. Position/Rotation/Scale as one x/y/z table, the
  // scale-lock and pivot as their own rows below it, mode controls (Face
  // the camera) last since they change what the rows above even mean. ────
  function buildPlace(body){
    /* Shown right above the table it changes the meaning of: once linked,
       Position/Rotation/Scale below stop being world numbers and become
       relative to this — the one thing someone reading those rows needs to
       know first, so it sits here rather than in the header with Name. */
    const linkParent = o.parentId != null ? S.objects.find(p=>p.id===o.parentId) : null;
    if(linkParent){
      const unlinkBtn = el('button','btn btn--sm btn--w','Linked to '+linkParent.name+' · Unlink');
      unlinkBtn.onclick = ()=>{ unlinkObject(o.id); renderRight(); requestRender(); };
      const linkRow = fieldRow('Linked to', unlinkBtn);
      linkRow.querySelector('label').classList.add('rowicon');
      linkRow.classList.add('rowunit');
      body.appendChild(linkRow);
    }else if(o.type !== 'camera'){
      /* The After Effects move, one click: a null where the object is, and the
         object linked to it. Sits where "Linked to" would, so after the click
         this row simply becomes that one. */
      const nb = el('button','btn btn--sm btn--gh btn--w','Parent to a new null');
      nb.title = 'Adds a null at this object and links the object to it — then move, turn or scale the null to carry it';
      nb.onclick = ()=>{ parentToNewNull(o.id); renderRight(); renderLeft(); requestRender(); };
      const parentRow = fieldRow('Parent', nb);
      parentRow.querySelector('label').classList.add('rowicon');
      parentRow.classList.add('rowunit');
      body.appendChild(parentRow);
    }
    const rows = [];
    rows.push({label:'Position', obj:o.pos, keys:['x','y','z'], step:.05, unit:'m', onChange:()=>upd('pos'), prop:'pos'});
    if(o.type !== 'light' && o.type !== 'camera' && !o.faceCamera)
      rows.push({label:'Rotation', obj:o.rot, keys:['x','y','z'], step:1, unit:'°', onChange:()=>upd('rot'), prop:'rot'});
    // Point and ambient lights have no beam to aim — only spot (and any
    // legacy directional light already saved to a project) has a direction.
    if(o.type === 'light' && (o.params.kind === 'spot' || o.params.kind === 'directional'))
      rows.push({label:'Rotation', obj:o.rot, keys:['x','y','z'], step:1, unit:'°', onChange:()=>upd('rot'), prop:'rot'});
    if(o.type !== 'light' && o.type !== 'camera')   // a lamp or viewpoint has no size
      rows.push({label:'Scale', obj:o.scale, keys:['x','y','z'], step:.01, unit:'×', prop:'scale', onChange:k=>{
        if(o.lockScale && k){ const v=o.scale[k]; o.scale.x=o.scale.y=o.scale.z=v; refreshInspectorValues(); }
        upd('scale');
      }});
    const grid = transformGrid(rows, inspectorRefs);
    // Each row is wrapped in its own display:contents div (see
    // transformGrid) precisely so it can still be found and keyframed on
    // its own, despite reading as one table.
    rows.forEach(r=>{ if(r.prop) stopwatch(r.rowEl, o, r.prop); });
    body.appendChild(grid);

    if(o.type !== 'light' && o.type !== 'camera'){
      const scaleAxes = segRow('Scale axes', o.lockScale ? 'together' : 'separately', [
        ['together','Together'],['separately','Separately']
      ], v=>{ o.lockScale = v==='together'; });
      // No stopwatch of its own to make room for — .rowicon reserves the
      // same space anyway, so its label's TEXT starts under Position's,
      // not just its column. See the note on .mtx .mrow>label in app.css.
      scaleAxes.querySelector('label').classList.add('rowicon');
      scaleAxes.classList.add('rowunit');   // matches the grid's own value-column width, both edges
      body.appendChild(scaleAxes);
    }
    if(canPivot(o)) body.appendChild(pivotSummary(o, ()=>{ renderRight(); requestRender(); pushHistory(); }));
    if(o.type !== 'light' && o.type !== 'camera' && o.type !== 'null'){
      const faceBtn = el('button', 'btn btn--sm btn--w '+(o.faceCamera ? 'btn--pri' : 'btn--gh'),
        o.faceCamera ? 'Facing the camera' : 'Face the camera');
      faceBtn.onclick = () => {
        o.faceCamera = !o.faceCamera;
        if(o.faceCamera && o.faceRoll==null) o.faceRoll=0;
        syncTransform(o); renderRight(); requestRender();
      };
      body.appendChild(faceBtn);
      if(o.faceCamera){
        body.appendChild(numRow('Roll', o.faceRoll||0, 1, v=>{
          o.faceRoll = v; syncTransform(o); requestRender();
        }, '°'));
        body.appendChild(el('p','note','Faces the viewer in On-site — Rotation doesn’t apply while this is on (that’s why it’s gone from the table above), only Roll, which spins it in its own plane. Position still moves it normally. Orbit doesn’t rotate it to match — spinning it as you orbit would hide its real position — so a small arrow points toward the viewer instead while it’s selected.'));
      }
    }
    const br = el('div','btnrow');
    const b1 = el('button','btn btn--sm btn--gh','On the screen');
    b1.title = 'Put it flat on the screen plane (z = 0)';
    b1.onclick = ()=>{ o.pos.z=0; upd('pos'); renderRight(); pushHistory(); };
    const b2 = el('button','btn btn--sm btn--gh','Centre');
    b2.onclick = ()=>{ o.pos.x=0; o.pos.y=0; upd('pos'); renderRight(); pushHistory(); };
    br.appendChild(b1); br.appendChild(b2);
    body.appendChild(br);
  }

  /* A null is Place and nothing else: it has no form, no surface and never
     renders. What it does have is children, so it says which. */
  if(o.type === 'null'){
    const pPlace = panel('Place');
    buildPlace(pPlace.body);
    R.appendChild(pPlace);
    const kids = S.objects.filter(c=>c.parentId===o.id);
    const pN = panel('Null', kids.length ? kids.length + ' linked' : 'nothing linked');
    pN.body.appendChild(el('p','note', kids.length
      ? 'Moving, turning or scaling this null carries ' + kids.map(k=>k.name).join(', ') + '.'
      : 'Nothing is linked to it yet. Select an object and press Parent to a new null in its Place panel, or drag an object onto this null in the Scene list.'));
    pN.body.appendChild(el('p','note','Drawn here as a marker only. It never appears in an export.'));
    R.appendChild(pN);
    return;
  }

  if(o.type === 'camera'){
    const pPlace = panel('Place');
    buildPlace(pPlace.body);
    R.appendChild(pPlace);

    const pC = panel('Viewpoint', 'off-axis');
    const e = eyeVec();
    pC.body.appendChild(readout([
      ['Distance out', fmt(e.z,3)+' m','hi'],
      ['Across', fmt(e.x,3)+' m'],
      ['Height', fmt(e.y,3)+' m']
    ]));
    const cn = el('p','note');
    cn.innerHTML = 'This camera <b>is</b> the viewer. Drag it and the render follows. '+
      'It has no rotation because an off-axis projection keeps the optical axis square '+
      'to the screen and shears the frustum instead — turning the camera would '+
      'misrepresent what the screen actually shows.';
    pC.body.appendChild(cn);
    const cb = el('button','btn btn--sm btn--gh btn--w','Put it back square in front');
    cb.onclick = ()=>{
      o.pos = {x:0, y:0, z: Math.hypot(S.screen.w,S.screen.h)*1.5};
      syncTransform(o); rebuildHelpers(); renderRight(); requestRender(); pushHistory();
    };
    pC.body.appendChild(cb);
    R.appendChild(pC);
    return;
  }

  if(o.type === 'light'){
    const pPlace = panel('Place');
    buildPlace(pPlace.body);
    R.appendChild(pPlace);

    const pL = panel('Light', P.kind);
    // 'directional' is no longer offered to new lights (see buildLight) but a
    // project saved before this change may still have one selected — keep it
    // choosable in that case rather than silently reassigning its kind.
    const kindOpts = [['spot','Spot — cone'],['point','Point — omni'],['ambient','Ambient — fills the scene']];
    if(P.kind === 'directional') kindOpts.push(['directional','Directional — parallel (legacy)']);
    pL.body.appendChild(selectRow('Type', P.kind, kindOpts, v=>{P.kind=v; rebuild(); renderRight();}));
    pL.body.appendChild(stopwatch(colorRow('Colour', o.color, v=>{o.color=v; captureKey(o,'color'); rebuild(); renderLeft();}), o, 'color'));
    pL.body.appendChild(stopwatch(numRow('Intensity', P.intensity,
      (P.kind==='directional'||P.kind==='ambient') ? .1 : 25,
      v=>{P.intensity=Math.max(0,v); captureKey(o,'intensity'); rebuild();},
      (P.kind==='directional'||P.kind==='ambient') ? 'lx' : 'cd'), o, 'intensity'));
    if(P.kind === 'spot'){
      pL.body.appendChild(numRow('Cone angle', P.angle, 1, v=>{P.angle=clamp(v,1,89); rebuild();}, '°'));
      pL.body.appendChild(sliderRow('Penumbra', P.penumbra, 0, 1, .01, v=>{P.penumbra=v; rebuild();}));
    }
    if(P.kind !== 'directional' && P.kind !== 'ambient'){
      pL.body.appendChild(numRow('Range', P.distance, .5, v=>{P.distance=Math.max(0,v); rebuild();}, 'm'));
      pL.body.appendChild(numRow('Decay', P.decay, .1, v=>{P.decay=Math.max(0,v); rebuild();}));
    }
    if(P.kind !== 'ambient')
      pL.body.appendChild(checkRow('Casts shadow', o.shadow, v=>{o.shadow=v; rebuild(); renderLeft();}));
    if(P.kind === 'spot' || P.kind === 'directional'){
      const ab = el('button','btn btn--sm btn--gh btn--w','Aim at screen centre');
      ab.onclick = ()=>{
        o.rot = aimEuler(new THREE.Vector3(o.pos.x,o.pos.y,o.pos.z), new THREE.Vector3(0,0,0));
        syncTransform(o); renderRight(); requestRender(); pushHistory();
      };
      pL.body.appendChild(ab);
    }
    const un = el('p','note');
    un.innerHTML = P.kind==='directional'
      ? 'A directional light is parallel rays from infinitely far — position only sets the direction, distance does nothing. Measured in <b>lux</b>, so single digits are normal.'
      : P.kind==='ambient'
      ? 'Ambient light has no position or direction — it lights every surface in the scene evenly, with no shadows. Measured in <b>lux</b>.'
      : 'Measured in <b>candela</b>, which falls off with distance squared — expect hundreds, not units. <b>Range 0</b> means no cutoff.';
    pL.body.appendChild(un);
    if(P.kind === 'spot' || P.kind === 'directional'){
      const rn2 = el('p','note');
      rn2.innerHTML = 'Rotate the light to aim it: the beam runs down its local <b>−Z</b>. The body only draws in Orbit, so it never lands in the export.';
      pL.body.appendChild(rn2);
    }
    R.appendChild(pL);
    return;
  }

  // ── Form — what shape it is. ───────────────────────────────────────────
  function buildForm(body){
    if(o.type==='recess'){
      /* AUTO-FILL HAS NO CONTROL ANY MORE, and deliberately still exists.
         The choice it offered was "fills the screen" against "custom", and
         nothing in the app ever picked the second one — both defaults in
         core/doc.js are autoFill:true and no template or preset writes it, so
         the only way in was this row. The reason to reach for Custom was
         almost always to make the visible hole smaller, which a border already
         does and which Only borders now does without disturbing the interior
         at all. An inset that genuinely does not fill its screen is a real but
         rare thing, and it did not earn a mode selector at the top of Form.

         The PARAM stays honoured in scene/objects.js's refitRecess, so a
         project saved with a hand-sized opening still opens at exactly the
         size it was saved with rather than silently snapping to the screen.
         "Fill the screen and re-centre" below sets it back to true, which is
         the way out of that state and the only writer left. */
      const auto = P.autoFill !== false;
      // each of these can change the outer size, so each re-fits while auto is on
      const bump = ()=>{ refitRecess(o); rebuild(); renderRight(); };

      /* Whether the border is part of the opening or sits on top of it. Framed,
         a thicker border eats into the recess — and on a corner into the depth,
         because the apertures ARE the depths there. Only borders keeps the
         inside at its zero-border size and lays the frame over it, so the band
         can be as fat as the design wants without touching the illusion behind
         it. Both keep per-side control; they are independent choices. */
      const ovl = P.borderOverlay === true;
      const ovlRow = segRow('Border sits', ovl ? 'over' : 'around', [
        ['around','Frames the opening'],['over','Only borders']
      ], v=>{
        P.borderOverlay = v==='over';
        refitRecess(o); rebuild(); renderRight();
      });
      const ovlInfo = infoToggle('Frames the opening: border and opening share the screen, so a wider border makes the opening — and on a corner the depth — smaller. Only borders: the opening stays the size it would be with no border at all, and the border is drawn over it.');
      ovlRow.appendChild(ovlInfo.btn);
      body.appendChild(ovlRow);
      body.appendChild(ovlInfo.note);

      /* Linked is the common case, so it stays a single field. Unlink to thicken one
         side - the opening slides to suit while the outer edge stays put, which is
         what keeps auto-fill honest. */
      const linked = P.linkBorder !== false;
      body.appendChild(segRow('Border', linked ? 'all' : 'side', [
        ['all','All round'],['side','Per side']
      ], v=>{
        const on = v==='all';
        P.linkBorder = on;
        if(!on){
          const b = Math.max(0, P.border||0);   // seed from the value in front of you
          if(P.borderT==null) P.borderT = b;
          if(P.borderR==null) P.borderR = b;
          if(P.borderB==null) P.borderB = b;
          if(P.borderL==null) P.borderL = b;
        }
        bump(); renderRight();
      }));
      if(!auto){
        /* Only a project saved before the Opening selector was removed can be
           in this state. The two fields stay so those numbers are visible and
           editable rather than frozen with nothing explaining why the inset
           has stopped following the screen — and the note says the way out. */
        body.appendChild(el('p','note','This inset was sized by hand, so it does not follow the screen. “Fill the screen and re-centre” below puts it back.'));
        body.appendChild(numRow('Opening width', P.w, .01, v=>{P.w=Math.max(.02,v); rebuild(); renderRight();}, 'm'));
        body.appendChild(numRow('Opening height', P.h, .01, v=>{P.h=Math.max(.02,v); rebuild(); renderRight();}, 'm'));
      }
      if(linked){
        body.appendChild(numRow('Border width', P.border, .01, v=>{P.border=Math.max(0,v); bump();}, 'm'));
      }else{
        [['Top','borderT'],['Right','borderR'],['Bottom','borderB'],['Left','borderL']].forEach(([lbl,key])=>{
          body.appendChild(numRow(lbl, P[key]==null ? (P.border||0) : P[key], .01,
            v=>{ P[key] = Math.max(0,v); bump(); }, 'm'));
        });
      }
      /* How thick the frame is, as opposed to how wide. Only exists overlaid:
         framed, the border IS the wall around the mouth and the material behind
         it runs the whole depth of the recess, so there is nothing to set.
         Shown falling back to the bevel while unset — which is what it renders
         as — so the field never reads 0 for a frame that plainly has thickness. */
      if(ovl)
        body.appendChild(numRow('Border depth', P.borderDepth==null ? (P.bevel||0) : P.borderDepth,
          .01, v=>{ P.borderDepth = Math.max(0,v); bump(); }, 'm'));
      /* DEPTH IS NOT A PARAMETER ON A CORNER. The notch is cut out of the
         corner itself, so how far it reaches into each panel IS that panel's
         aperture width — cornerL and cornerR, which auto-fill derives from the
         screen and the borders. P.depth is read by the flat builder and by
         nothing else here, so the field only ever looked like it did
         something. Hidden rather than shown disabled: a control that is
         inapplicable is not the same as one you have to earn, and the note
         below says where the depth actually comes from. */
      const corner = !!S.cornerPanel;
      if(!corner)
        body.appendChild(numRow('Depth', P.depth, .01, v=>{P.depth=Math.max(.001,v); bump();}, 'm'));
      body.appendChild(numRow('Bevel', P.bevel, .005, v=>{P.bevel=Math.max(0,v); bump();}, 'm'));
      if(corner)
        body.appendChild(el('p','note','No depth on a corner — the notch is cut out of the corner itself, so how far it reaches into each panel is set by that panel’s width and its border.'));

      const fb = el('button','btn btn--sm btn--gh btn--w','Fill the screen and re-centre');
      fb.onclick = ()=>{
        P.autoFill = true; refitRecess(o);
        o.pos.x = 0; o.pos.y = 0;
        rebuild(); renderRight(); pushHistory();
      };
      body.appendChild(fb);

      /* Each mode clamps the bevel against different quantities, so the
         warning has to as well — it was testing P.depth in both, which on a
         corner is the one number the geometry ignores. See the two clamps in
         scene/geometry.js. */
      /* Overlaid the fillet rolls inward off the frame instead of flaring the
         mouth outward, so it has the VISIBLE opening to fit inside rather than
         the whole recess — the same extra terms both builders clamp against. */
      const BV = recessBorders(P);
      /* The frame's own thickness bounds the roll too — a border depth below the
         bevel clamps it, and that is the one way this warning can fire on a
         recess whose opening has plenty of room. Not scaled by .49, matching
         the builders: it is a depth, not half of a span. */
      const fdUI = P.borderDepth == null ? Math.max(0, P.bevel||0) : Math.max(0, P.borderDepth);
      const lim = corner
        ? Math.min(...(ovl ? [fdUI] : []),
            Math.min(P.cornerL || 0, P.cornerR || 0, (P.h||0)/2,
              ...(ovl ? [(P.cornerL||0) - BV.left, (P.cornerR||0) - BV.right,
                         (P.h||0)/2 - (BV.top + BV.bottom)/2] : [])) * .49)
        : Math.min(P.depth*.98, Math.min(P.w, P.h)*.49,
            ...(ovl ? [fdUI, Math.min(P.w - (BV.left + BV.right),
                                      P.h - (BV.top + BV.bottom)) * .49] : []));
      if((P.bevel||0) > lim + 1e-9)
        body.appendChild(el('p','note note--warn', ovl
          ? 'The bevel is being clamped — laid over the opening it has to fit inside what the border leaves, and it can’t be deeper than the border itself.'
          : corner
          ? 'The bevel is being clamped — on a corner it can’t be wider than half the shallower side of the notch.'
          : 'The bevel is being clamped — it can’t be deeper than the inset or wider than half the opening.'));
    }
    if(o.type==='frame'){
      /* Outer size is not a setting: it tracks the screen automatically (see
         refitFrame, called on every screen-size change) so a frame is never
         left mismatched with what it is bezelling. Border width — linked or
         per side — is the one thing left to choose, using the same convention
         and the same recessBorders() helper a recess's border already uses. */
      const linked = P.linkBorder !== false;
      body.appendChild(segRow('Border', linked ? 'all' : 'side', [
        ['all','All round'],['side','Per side']
      ], v=>{
        const on = v==='all';
        P.linkBorder = on;
        if(!on){
          const b = Math.max(0, P.border||0);
          if(P.borderT==null) P.borderT = b;
          if(P.borderR==null) P.borderR = b;
          if(P.borderB==null) P.borderB = b;
          if(P.borderL==null) P.borderL = b;
        }
        rebuild(); renderRight();
      }));
      if(linked){
        body.appendChild(numRow('Border width', P.border, .01, v=>{P.border=Math.max(0,v); rebuild();}, 'm'));
      }else{
        [['Top','borderT'],['Right','borderR'],['Bottom','borderB'],['Left','borderL']].forEach(([lbl,key])=>{
          body.appendChild(numRow(lbl, P[key]==null ? (P.border||0) : P[key], .01,
            v=>{ P[key] = Math.max(0,v); rebuild(); }, 'm'));
        });
      }
      const BB = recessBorders(P);
      const openW = Math.max(0, P.w - BB.left - BB.right);
      const openH = Math.max(0, P.h - BB.top - BB.bottom);
      const hasOpening = openW > 1e-4 && openH > 1e-4;
      if(!hasOpening) body.appendChild(el('p','note note--warn','The borders meet in the middle — this is a solid black panel with no opening.'));
      /* The "add an image texture" control was here and has been removed.
         o.map, the generic per-object texture slot it wrote to, is deliberately
         left in place: scene/textures.js still applies one, and doc.js,
         projects.js and history.js still carry it, so a project saved with a
         textured frame still opens looking the way it was left. Nothing
         creates a new one. */
      body.appendChild(el('p','note','A flat bezel, black by default, fixed centred just in front of the inset and sized to the screen automatically. Border width is the only thing that shapes it — widen one side more than another to frame it unevenly.'));
    }
    if(o.type==='text') textGeometryRows(body, {o, rebuild, renderRight});
    if(o.type==='textarea') textAreaRows(body, {o, rebuild, renderRight});
    if(o.type==='video'){
      body.appendChild(numRow('Width', P.w, .01, v=>{P.w=v; rebuild();}, 'm'));
      body.appendChild(el('p','note','Height follows the clip’s own aspect ratio.'));
      body.appendChild(swapFileRow(o));
      body.appendChild(el('div','rule'));

      /* Depth and bevel, the same pair 3D Text has. A clip on a flat plane
         reads as a decal printed on the back of the recess; given real
         thickness it reads as a screen SET INTO it, which is the illusion the
         whole app is for. */
      body.appendChild(numRow('Depth', P.depth, .005, v=>{P.depth=Math.max(0,v); rebuild(); renderRight();}, 'm'));
      if(P.depth > 1e-4){
        body.appendChild(numRow('Bevel', P.bevel, .002, v=>{P.bevel=Math.max(0,v); rebuild();}, 'm'));
        body.appendChild(el('p','note','The bevel rounds the edge outward, so it adds its own size to the panel — the same way 3D Text’s does.'));
        /* No opt-in. An extruded clip always has a second material for its
           edge — the picture is flat-lit and the edge is not, so the two could
           never have shared one — which leaves nothing for a checkbox to
           decide. It was there when the edge merely inherited the face. */
        body.appendChild(colorRow('Edge colour', o.sideColor||'#101010', v=>{o.sideColor=v; rebuild();}));
      } else {
        body.appendChild(el('p','note','At zero depth this is a flat plane. Give it depth to stand it off the back wall like a real panel.'));
      }
      body.appendChild(el('div','rule'));

      /* Timing. Kept in Form rather than a panel of its own: these are
         properties of the clip, and a panel per concept would mean three
         clicks to see five fields. See core/video-time.js for the model
         these four feed. */
      /* Every one of these changes WHICH FRAME belongs at the current playhead,
         so they have to re-run the evaluation, not just ask for a redraw — a
         redraw would faithfully draw the frame already on the texture. */
      const retime = () => { applyAnimation(); requestRender(); };
      body.appendChild(numRow('Starts at', P.offset, .1, v=>{P.offset=v; retime();}, 's'));
      body.appendChild(numRow('Trim start', P.start, .1, v=>{
        P.start=Math.max(0,Math.min(v, Math.max(0,P.srcDur-.05))); retime(); renderRight();
      }, 's'));
      body.appendChild(numRow('Speed', P.speed, .05, v=>{P.speed=Math.min(8,Math.max(.05,v)); retime();}, '×'));
      body.appendChild(checkRow('Loop', P.loop !== false, v=>{P.loop=v; retime();}));
      /* The source rate matters because nothing converts it. Frames are sampled
         on a continuous time axis (core/video-time.js), so a clip whose rate is
         a multiple of the project's — or the other way round — lands cleanly,
         and one that is neither has to repeat a frame here and there. Measured
         on a 24fps clip in a 30fps project: one frame in five shown twice. That
         judder is not a bug and cannot be sampled away, but it should never be
         a mystery. */
      const srcFps = Math.round(P.srcFps || 0);
      const projFps = S.timeline.fps;
      const divides = !srcFps || srcFps === projFps ||
        (srcFps > projFps ? srcFps % projFps === 0 : projFps % srcFps === 0);
      body.appendChild(el('p','note',
        P.srcDur ? `The clip is ${P.srcDur.toFixed(2)}s of ${P.srcW}×${P.srcH}` +
                   (srcFps ? ` at ${srcFps}fps.` : ' footage.')
                 : 'Reading the clip’s details…'));
      if(!divides){
        body.appendChild(el('p','note note--warn',
          `This project runs at ${projFps}fps, which ${srcFps} does not divide into — so some frames ` +
          'of the clip are shown twice and the motion stutters slightly. Matching the project to the ' +
          'clip removes it entirely: nothing is dropped, and nothing is invented.'));
        /* Only offered for a rate the project can actually be set to — the fps
           field is a fixed list, and a button that silently did nothing would
           be worse than no button at all. */
        if(TIMELINE_FPS.includes(srcFps)){
          const b = el('button','btn btn--sm btn--w', `Set the project to ${srcFps}fps`);
          b.onclick = ()=>{
            S.timeline.fps = srcFps;
            /* The playhead sat on a frame boundary of the OLD rate and is now
               between two of the new one — the same correction the timeline's
               own fps field makes. */
            setPlayhead(quantise(S.playhead));
            pushHistory();
            emit('anim:changed');            // rebuilds the dock and its fps field
            renderRight();
            requestRender();
          };
          body.appendChild(b);
        }
      }
      body.appendChild(el('p','note','Audio is ignored — signage plays muted, and the export writes no audio track.'));
    }
    if(o.type==='image'){
      body.appendChild(numRow('Width', P.w, .01, v=>{P.w=v; rebuild();}, 'm'));
      body.appendChild(sliderRow('Alpha cutoff', P.cutoff, 0, .9, .01, v=>{P.cutoff=v; rebuild();}));
      body.appendChild(el('p','note','Height follows the file’s aspect ratio. Transparent PNGs keep their alpha.'));
      body.appendChild(swapFileRow(o));
      body.appendChild(el('div','rule'));
      const masked = !!(o.mask && o.mask.on);
      const mrow = el('div','btnrow');
      const eb = el('button','btn btn--sm', masked ? 'Edit mask' : 'Add mask');
      eb.onclick = ()=>openMask(o);
      mrow.appendChild(eb);
      if(masked){
        const cb = el('button','btn btn--sm btn--gh','Clear');
        cb.onclick = ()=>{ o.mask.on=false; syncNode(o); renderRight(); requestRender(); pushHistory(); };
        mrow.appendChild(cb);
      }
      body.appendChild(mrow);
      body.appendChild(el('p','note', masked
        ? (o.mask.invert ? 'Masked: the drawn area is hidden.' : 'Masked: only the drawn area shows.')
        : 'Draw the mask directly on the current view — On-site draws it against the real building, so a subject reads as popping out over the inset edge.'));
    }
    /* A basic shape: which one, and the sizes that shape has (SHAPES in
       core/doc.js says which). Every size is kept on the object, so switching
       a cube to a cylinder lands on a matching size, and switching back finds
       the cube as it was. */
    if(o.type==='shape'){
      const P = o.params;
      body.appendChild(selectRow('Shape', P.shape,
        Object.entries(SHAPES).map(([k, s])=>[k, s.label]),
        v=>{
          /* "Sphere 1" turned into a cylinder should not stay "Sphere 1". Only a
             name the app gave it is changed; one somebody typed is theirs. */
          const auto = new RegExp('^' + SHAPES[P.shape].label + ' \\d+$').test(o.name || '');
          P.shape = v;
          if(auto) o.name = SHAPES[v].label + ' ' + S.objects.filter(x=>x.type==='shape' && x.params.shape===v).length;
          rebuild(); renderRight(); renderLeft(); pushHistory();
        }));
      const LABEL = { w:'Width', h:P.shape==='capsule' ? 'Height (overall)' : 'Height', d:'Depth',
                      r:'Radius', tube:'Thickness', round:P.shape==='box' ? 'Corner radius' : 'Edge radius' };
      for(const k of SHAPES[P.shape].sizes){
        const min = k === 'round' ? 0 : .001;
        body.appendChild(numRow(LABEL[k], P[k], .01, v=>{ P[k] = Math.max(min, v); rebuild(); }, 'm', 'shape-' + k));
      }
      if(SHAPES[P.shape].sizes.includes('round'))
        body.appendChild(el('p','note','A little rounding catches the light along each edge, which is what makes a shape read as a solid object rather than a flat drawing. 0 keeps the edges sharp.'));
      body.appendChild(swapFileRow(o));
      body.appendChild(el('p','note','Standing in for a product? Replace it with the real model when it arrives: it takes this shape’s place, size and keyframes.'));
    }
    if(o.type==='model'){
      body.appendChild(swapFileRow(o));
      body.appendChild(checkRow('Casts shadow', o.shadow!==false, v=>{o.shadow=v; rebuild();}));
      body.appendChild(el('p','note','Imported with its own baked materials and textures. Scale and rotate it with the gizmo, or drop it inside the inset for anamorphic depth.'));
    }
  }

  if(o.type==='frame'){
    const pForm = panel('Form');
    buildForm(pForm.body);
    R.appendChild(pForm);
    return;   // no lighting/shadow controls — genuinely unlit either way
  }

  // ── Look — what it's made of. ──────────────────────────────────────────
  function buildLook(body){
    body.appendChild(stopwatch(colorRow(o.type==='image'||o.type==='textarea'||o.type==='video'?'Tint':'Colour', o.color, v=>{o.color=v; remat();}), o, 'color'));
    /* Only image for now — see the user-facing request this answers. The
       property and the material-side handling (makeMaterial's
       applyBlendMode, scene/objects.js) are generic to every type, so
       widening this to text/video/model later is one more `if` here, not
       new plumbing. Placed as its own thing rather than folded into
       Finish: it composites against what's BEHIND the object, where
       Finish/Roughness describe the surface's own response to light —
       two different questions that happen to both be about "how it looks". */
    if(o.type==='image'){
      body.appendChild(selectRow('Blend mode', o.blend || 'normal',
        Object.entries(BLEND_NAMES), v=>{ o.blend=v; rebuild(); }));
      body.appendChild(el('p','note','How this image combines with whatever sits behind it — Multiply darkens, Screen lightens, Add and Subtract push the sum or difference, Darken/Lighten keep whichever of the two is darker or lighter at each pixel. Photoshop’s Overlay, Soft Light and Hard Light aren’t offered: those blend WITHIN a pixel rather than between two layers, which needs a shader pass the GPU’s own blend hardware can’t do on its own.'));
    }
    /* Flat colour is Blender's Emission: the surface is exactly the chosen hex.
       Metal and Rough have no meaning once lighting is out of the picture, so
       they are hidden rather than left there doing nothing — shadow-casting is
       unaffected and stays available either way (see below). */
    /* A clip's PICTURE is always flat-lit and there is no choice about it (see
       buildVideo) — so for a video these describe its EDGE, and with no depth
       there is no edge and nothing here left to decide. */
    const vid = o.type === 'video';
    const edged = vid && o.params.depth > 1e-4;
    if(vid)
      body.appendChild(el('p','note', edged
        ? 'The picture is always rendered flat, so it exports exactly the pixels the clip came with. These describe its edge.'
        : 'The picture is always rendered flat, so it exports exactly the pixels the clip came with. Give it Depth to get an edge worth shading.'));
    if(!vid || edged)
      body.appendChild(checkRow(vid ? 'Flat edge (unlit)' : 'Flat colour (unlit)', !!o.unlit, v=>{
        o.unlit = v; rebuild(); renderRight();
      }));
    const shaded = (vid ? edged : true) && !o.unlit;
    if(shaded){
      body.appendChild(segRow('Finish', o.metalness>=.5 ? 'metal' : 'matte', [
        ['matte','Matte'],['metal','Metal']
      ], v=>{ o.metalness = v==='metal' ? 1 : 0; remat(); }));
      body.appendChild(sliderRow('Roughness', o.roughness, 0,1,.01, v=>{o.roughness=v; remat();}));
    }
    body.appendChild(stopwatch(sliderRow('Opacity', o.opacity, 0,1,.01, v=>{o.opacity=v; remat();}), o, 'opacity'));
    if(!shaded && !vid)
      body.appendChild(el('p','note','No lighting, no reflections — the render shows this exact colour, untouched by exposure or tone mapping.'));
    // Casting a shadow is a geometric-occlusion property, independent of whether
    // the surface itself responds to lighting — an unlit object can still block
    // light headed for something else, so this stays outside the branch above.
    body.appendChild(segRow('Shadow', o.shadow ? 'casts' : 'none', [
      ['casts','Casts'],['none','None']
    ], v=>{ o.shadow = v==='casts'; rebuild(); }));

    if(o.type==='text') textSurfaceRows(body, {o, rebuild, renderRight});
  }

  if(o.type==='model'){
    const panes = [];
    const placePane = el('div','panel__bd'); buildPlace(placePane); panes.push(['Place', placePane]);
    const formPane = el('div','panel__bd'); buildForm(formPane); panes.push(['Form', formPane]);
    R.appendChild(tabStrip(panes, {key:'inspector'}));
    panes.forEach(([,node])=>R.appendChild(node));
    return;      // no Look tab — a model brings its own baked materials
  }

  /* The editor is what a Text Area IS — it belongs right under the header,
     not behind a tab click past a width field and a tint swatch, to reach
     the words you actually came here to change. */
  if(o.type==='textarea') R.appendChild(textAreaContentPanel({o, rebuild, renderRight}));

  const panes = [];
  // Neither fixedTransform type gets a Place tab — see the comment on that
  // flag above: once Name moved into the header, there was nothing left in
  // it for a recess or a frame.
  if(!fixedTransform){
    const placePane = el('div','panel__bd'); buildPlace(placePane); panes.push(['Place', placePane]);
  }
  const formPane = el('div','panel__bd'); buildForm(formPane); panes.push(['Form', formPane]);
  const lookPane = el('div','panel__bd'); buildLook(lookPane);
  /* Outline, drop shadow and outer glow, INSIDE Look rather than below the
     tab strip. It used to be appended after the panes, which put it outside
     all three — so it followed you onto Place and Form, where it made no
     sense, and Look ended one section short of the thing it is about.
     Effects are appearance; this is the tab for appearance.

     Kept as its own collapsible panel rather than poured into the body: it
     is three independent effects with their own sub-controls, and it is the
     longest group in the inspector. Text Area shares the exact same panel
     and param names as 3D Text (DEF.textarea's own fx* fields) — only the
     outline note differs, since a Text Area's outline is a canvas stroke,
     not extruded geometry. */
  if(o.type==='text' || o.type==='textarea' || o.type==='image' || o.type==='video')
    lookPane.appendChild(effectsPanel({o, rebuild, renderRight}));
  panes.push(['Look', lookPane]);
  R.appendChild(tabStrip(panes, {key:'inspector'}));
  panes.forEach(([,node])=>R.appendChild(node));
}


/* SCENE SETTINGS — everything about the scene that isn't a specific object,
   sorted by who owns the number rather than stacked at one weight.

   Pixels, metres, lens and viewpoint used to be editable here AND in
   Calibrate — and the viewpoint is also moved by the On-site camera rig.
   Two editable copies of one number is how they come apart, with no answer
   to which is true. So: does a number change the exported file? It stays
   here, editable. Does it describe the real installation? It moves to a
   reading, with a way through to the one place — Calibrate — that owns it.

   Shown whenever nothing is selected — which, now that it's useful instead
   of a bare "click something" placeholder, is also just what's on screen
   most of the time between edits. */
function renderMasterSettings(R){
  // ── Changes the export ───────────────────────────────────────────────
  const p1 = panel('Changes the export');

  const gLight = el('div','group');
  gLight.appendChild(selectRow('Lighting', S.lighting, [
    ['dark','Dark — light it yourself'],['studio','Studio'],['sun','Hard sun'],
    ['night','Night'],['flat','Flat graphic']
  ], v=>{
    S.lighting=v; S.lightAz=null; S.lightEl=null;   // each preset opens at its own default rotation
    applyLighting(true); renderRight(); requestRender();
  }));
  gLight.appendChild(sliderRow('Shadow blur', S.shadowSoft, 0, 20, .5, v=>{setShadowSoft(v);}));
  if(S.lighting !== 'dark'){
    const sun = effectiveLightAzEl();
    gLight.appendChild(sliderRow('Sun azimuth', sun.az, 0, 360, 1, v=>{S.lightAz=v; applyLighting(false); requestRender();}));
    gLight.appendChild(sliderRow('Sun elevation', sun.el, 1, 89, 1, v=>{S.lightEl=v; applyLighting(false); requestRender();}));
    if(S.lightAz!=null || S.lightEl!=null){
      const rb = el('button','btn btn--sm btn--gh btn--w','Reset sun rotation');
      rb.onclick = ()=>{ S.lightAz=null; S.lightEl=null; applyLighting(false); renderRight(); requestRender(); pushHistory(); };
      gLight.appendChild(rb);
    }
    gLight.appendChild(sliderRow('Environment', S.envInt, 0, 3, .01, v=>{S.envInt=v; applyEnvIntensity(); requestRender();}));
  }
  const lampCount = S.objects.filter(x=>x.type==='light' && x.visible).length;
  const shadowCount = S.objects.filter(x=>x.type==='light' && x.visible && x.shadow).length;
  const ln = el('p','note');
  if(S.lighting === 'dark'){
    if(lampCount){
      ln.innerHTML = 'Nothing but your own lights — no environment, no ambient, no key. <b>'+
        lampCount+'</b> light'+(lampCount===1?'':'s')+' in the scene.';
    }else{
      ln.className = 'note note--warn';
      ln.innerHTML = '<b>Nothing is lit.</b> Dark means no environment, no ambient and no key light. Add a <b>Light</b> from the palette on the left.';
    }
  }else{
    ln.innerHTML = 'Each preset also builds an <b>environment map</b>, so surfaces have something real to reflect. Turn Environment to 0 and you get the flat, plastic look of direct lights only.';
  }
  gLight.appendChild(ln);
  if(shadowCount > 3)
    gLight.appendChild(el('p','note note--warn',shadowCount+' lights are casting shadows. Each one renders its own map every frame — point lights six times over — so turn some off if it starts to crawl.'));
  /* Every preset, Dark included — it shades creases and contact, which a
     scene lit only by your own lamps has just as much as a studio one. Reach
     stays visible at strength 0: showing it only once strength moves would
     rebuild the panel under a slider that is still being dragged. Below the
     lighting notes, which are about the preset and Environment above them. */
  gLight.appendChild(sliderRow('Occlusion', S.aoStrength, 0, 1, .05, v=>{S.aoStrength=v; requestRender();}));
  gLight.appendChild(sliderRow('Occlusion reach', S.aoReach, .02, .5, .01, v=>{S.aoReach=v; requestRender();}));
  p1.body.appendChild(gLight);

  /* DEPTH OF FIELD (scene/dof.js). One Blur slider and a subject to keep sharp,
     rather than f-stops: judged by eye, and the same on any screen size. The
     focus picker lists the things worth focusing on — the fixtures and the
     viewer are left out — and follows the chosen one through an animation. */
  const gDof = el('div','group');
  gDof.appendChild(el('div','group__hd','Depth of field'));
  gDof.appendChild(sliderRow('Blur', S.dofStrength, 0, 1, .05, v=>{S.dofStrength=v; requestRender();}));
  const F = S.dofFocus || (S.dofFocus = { mode:'screen', z:0, id:null });
  const focusable = S.objects.filter(x=>!['camera','light','recess','frame'].includes(x.type));
  const current = F.mode === 'object' && focusable.some(x=>x.id === F.id) ? 'obj:' + F.id
                : F.mode === 'custom' ? 'custom' : 'screen';
  gDof.appendChild(selectRow('Focus on', current, [
    ['screen', 'The screen surface'],
    ['custom', 'A depth I set'],
    ...(focusable.length ? [[null, 'Objects']] : []),
    ...focusable.map(x=>['obj:' + x.id, x.name || NICE[x.type]])
  ], v=>{
    if(v.startsWith('obj:')) Object.assign(F, { mode:'object', id:+v.slice(4) });
    else Object.assign(F, { mode:v, id:null });
    requestRender(); renderRight(); pushHistory();
  }));
  if(current === 'custom')
    gDof.appendChild(numRow('Focus depth', F.z, .05, v=>{ F.z = v; requestRender(); }, 'm', 'dof-z'));
  gDof.appendChild(el('p','note', S.dofStrength > 0
    ? (current === 'custom'
        ? 'In metres from the screen surface: positive is in front of it, negative is back into the inset.'
        : 'Things nearer or further than the focus go soft, more the further they are from it. The focus follows an object when it moves.')
    : 'Off. Raise Blur to let things away from the focus go soft, the way a lens does. It makes a deep inset read deeper.'));
  p1.body.appendChild(gDof);

  // What used to be Export's own "Deliverable" panel, still exclusive to it —
  // Width/Height/Supersample live in Calibrate's Screen panel and the
  // Settings modal instead, so only the export background itself belongs here.
  const gBg = el('div','group');
  gBg.appendChild(el('div','group__hd','Behind it'));
  gBg.appendChild(segRow('', S.bg.transparent ? 'transparent' : 'colour', [
    ['colour','Colour'],['transparent','Transparent']
  ], v=>{ S.bg.transparent = v==='transparent'; renderRight(); requestRender(); }, {key:'Behind it'}));
  if(!S.bg.transparent) gBg.appendChild(colorRow('', S.bg.color, v=>{S.bg.color=v; requestRender();}));
  gBg.appendChild(el('p','note','What fills the screen behind your content in the exported file — not the colour of this workspace.'));
  p1.body.appendChild(gBg);

  // Orbit/Split (isOrbitLike) is where the eye icon actually lives and can
  // be dragged; On-site has no icon to grab (see ui/interaction.js's
  // camFor(ev)===orbitCam guard) but the SAME S.nudge reaches its render
  // too, so the offset fields belong here as well, not just in Orbit.
  if(isOrbitLike() || S.view==='site'){
    const gView = el('div','group');
    gView.appendChild(el('div','group__hd','Viewer offset'));
    const e = eyeVec();
    gView.appendChild(readout([
      ['Distance out', fmt(e.z,3)+' m','hi'],
      ['Across', fmt(e.x,3)+' m'],
      ['Height', fmt(e.y,3)+' m']
    ]));
    gView.appendChild(el('p','note', cameraObj()
      ? 'The Camera object is driving the viewpoint — drag it in the scene. These offset fields only apply when the viewer came from a calibrated photo.'
      : (S.view==='orbit'
          ? 'Drag the eye icon here in Orbit to move the viewpoint directly — hold Shift to adjust distance instead of position. It reaches Flat and On-site too, without ever needing the angle re-locked.'
          : 'Switch to Orbit to drag the eye icon directly, or type exact values here instead — either way it reaches Flat and On-site too, without ever needing the angle re-locked.')));
    /* Not a calibration field: the solve finds where the photographer stood,
       and this is "and a bit further out than that" — authored, not
       measured. Because the deliverable is rendered from the eye, moving it
       changes the export, so it stays here rather than in the read-only
       block below. The Calibrate camera rig On-site drags this same value. */
    gView.appendChild(vec3Row('Viewer offset', S.nudge, ['x','y','z'], .05, ()=>{rebuildHelpers();requestRender();renderRight();}, inspectorRefs));
    const rb = el('button','btn btn--sm btn--gh btn--w','Reset viewer offset');
    rb.onclick = ()=>{ S.nudge={x:0,y:0,z:0}; rebuildHelpers(); renderRight(); requestRender(); pushHistory(); };
    gView.appendChild(rb);

    /* ON-SITE ONLY, and off by default. The widening it controls is keyed off
       the SELECTION, so while it is on, every click between something on the
       screen and something off it resizes the view and takes the dashed
       deliverable edge with it. That is useful when you are reaching for a
       light above the sign and disorienting the rest of the time, which is
       most of the time — so it is asked for rather than assumed.

       Here rather than in a menu because Scene Settings is the one row the
       outliner always offers: the first attempt at a control lived somewhere
       the inspector replaced as soon as anything was selected, which meant it
       could not be reached with a light selected — exactly when it was
       wanted. */
    if(isSiteLike()){
      gView.appendChild(el('div','rule'));
      gView.appendChild(checkRow('Reach outside the screen', !!S.siteReach, v=>{
        S.siteReach = v; renderRight(); requestRender();
      }));
      gView.appendChild(el('p','note', S.siteReach
        ? 'The view widens to reach whatever is selected when it sits outside the screen, and the dashed line marks where the deliverable actually ends. It resizes as you select different things.'
        : 'The view stays on the deliverable, whatever is selected. Turn this on to reach something parked outside the screen — a light above the sign, most often — without leaving On-site.'));
      gView.appendChild(checkRow('Show outlines and null markers', S.siteOutlines !== false, v=>{
        S.siteOutlines = v; requestRender();
      }));
      gView.appendChild(el('p','note', 'The selection outline and the markers of null objects, drawn over the photograph. Off, the mockup shows only the work; the handles for moving the selection stay.'));
    }
    p1.body.appendChild(gView);
  }
  R.appendChild(p1);

  // ── Describes the installation ───────────────────────────────────────
  /* These were read-only for a while, on the reasoning that one number should
     have one editable home and Calibrate already owned it. That reasoning does
     not survive SET THE VIEWER BY HAND (ui/calibrate-view.js's manualSetup):
     there is no photo on that path and no corners to mark, so Calibrate is a
     screen built around marking a shot you never took — and the only place the
     screen's own size could be typed sat behind it, under a "Corners — load a
     reference photo" heading. Sizing the display you are designing for is not
     a calibration step and should not have needed one.

     They are not two copies of a number either, which is what the earlier
     worry actually guarded against: both editors write the same S.screen/S.res
     through onScreenChange, exactly as the fields in Calibrate do, and the
     stale warning below already catches a solve that has fallen behind. */
  const byHand = !S.photo;
  const p2 = panel('Describes the installation',
    S.screenArray?.mode === 'free' ? `${S.screenArray.screens.length} screens`
    : S.screenArray ? `${S.screenArray.cols} × ${S.screenArray.rows} screens`
                  : byHand ? 'set by hand' : 'set in Calibrate');
  if(S.eye){
    const focalTxt = S.focal.mode==='manual'
      ? fmt(S.focal.manualMM,0)+' mm equiv. (typed)'
      /* Not "Recovering…" — with no photo there is no quad and nothing is ever
         going to arrive, so the old text sat there implying work in progress
         for the whole life of a by-hand project. */
      : byHand ? 'No photo — viewer placed by hand'
      : S.focal.px!=null ? Math.round(S.focal.px/S.photo.w*36)+' mm equiv.'
      : 'Recovering…';
    p2.body.appendChild(readout([
      ['Screen', Math.round(S.res.w)+' × '+Math.round(S.res.h)+' px'],
      ['', fmt(S.screen.w,2)+' × '+fmt(S.screen.h,2)+' m'],
      ['Lens', focalTxt],
      [byHand ? 'Viewer at' : 'Solved from',
       fmt(S.eye.z,3)+' m out · '+fmt(S.eye.x,3)+' m across'],
    ]));
    if(!S.solved) p2.body.appendChild(el('p','note note--warn',
      'Changed since the angle was last locked — go to Calibrate and press Lock angle again to refresh the viewpoint. The corners themselves are untouched.'));
  }else{
    p2.body.appendChild(el('p','note','Not calibrated yet.'));
  }

  /* The screen itself, editable here again. Pixels and metres stay locked to
     one aspect the way Calibrate's own Grid/Size rows do — type either side and
     the other follows — so the two panels cannot disagree about the shape of
     the display even while both can set it.

     An ARRAY replaces these four with the grid that derives them. The wall is
     not typeable then, because it is not an input: it is cols x rows screens
     plus the margins between, and offering a second editable copy of the answer
     is exactly the drift this panel avoids everywhere else. The wall is still
     reported, in the readout above. */
  const arr = S.screenArray;
  p2.body.appendChild(el('div','rule'));
  if(!arr){
    p2.body.appendChild(numRow('Pixels across', S.res.w, 1, v=>{
      S.res.w = Math.max(1, Math.round(v)); S.screen.h = S.screen.w/pxAspect();
      onResChange(); onScreenChange(); renderRight(); renderLeft();
    }, 'px'));
    p2.body.appendChild(numRow('Pixels down', S.res.h, 1, v=>{
      S.res.h = Math.max(1, Math.round(v)); S.screen.h = S.screen.w/pxAspect();
      onResChange(); onScreenChange(); renderRight(); renderLeft();
    }, 'px'));
    p2.body.appendChild(numRow('Width', S.screen.w, .01, v=>{
      S.screen.w = Math.max(.02, v); S.screen.h = S.screen.w/pxAspect();
      onScreenChange(); renderRight(); renderLeft();
    }, 'm'));
    p2.body.appendChild(numRow('Height', S.screen.h, .01, v=>{
      S.screen.h = Math.max(.02, v); S.screen.w = S.screen.h*pxAspect();
      onScreenChange(); renderRight(); renderLeft();
    }, 'm'));
  }else{
    /* Scene Settings reports the wall and hands off. The fields themselves moved
       to the LCD Video Wall in the outliner, because one editable home is the rule
       this panel follows everywhere else — and because a list is where people
       look for a thing. */
    const wall = wallOf(arr);
    p2.body.appendChild(readout(arr.mode === 'free' ? [
      ['Layout', arr.screens.length + (arr.screens.length === 1 ? ' screen, placed freely' : ' screens, placed freely')],
      ['Wall', fmt(wall.w,2) + ' × ' + fmt(wall.h,2) + ' m overall'],
      ['Export', arr.screens.length + (arr.screens.length === 1 ? ' file' : ' files')]
    ] : [
      ['Grid', arr.cols + ' × ' + arr.rows + ' screens'],
      ['Each screen', arr.res.w + ' × ' + arr.res.h + ' px'],
      ['', fmt(arr.tile.w,2) + ' × ' + fmt(arr.tile.h,2) + ' m'],
      ['Margin', Math.round(arr.gap*1000) + ' mm'],
      ['Wall', fmt(wall.w,2) + ' × ' + fmt(wall.h,2) + ' m'],
      ['Export', arr.cols * arr.rows + ((arr.cols * arr.rows) === 1 ? ' file' : ' files')]
    ]));
    const goArr = el('button','ro__go','Edit in LCD Video Wall');
    goArr.innerHTML += ' <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 8h8M8.5 4.5 12 8l-3.5 3.5"/></svg>';
    goArr.onclick = ()=>{ select(null); S.arraySelected = true; renderRight(); renderLeft(); };
    p2.body.appendChild(goArr);
  }
  /* Recovering the lens from four marks is only a thing that can happen when
     there are four marks, so by hand the choice collapses to the number you
     type. Hidden rather than shown disabled, the same call the corner Depth
     field gets in the inspector above. */
  if(!byHand){
    p2.body.appendChild(selectRow('Focal length', S.focal.mode, [
      ['auto','Recover from the quad'],['manual','I know what I shot']
    ], v=>{ S.focal.mode = v; renderRight(); renderLeft(); }));
    /* With a photo this feeds the solve (core/calibrate.js turns it into
       pixels), so it stays a stored number. */
    if(S.focal.mode==='manual')
      p2.body.appendChild(numRow('35 mm equiv. (iPhone default)', S.focal.manualMM, .5,
        v=>{ S.focal.manualMM = v; renderRight(); }, 'mm', 'lensSolve'));
  }else{
    /* BY HAND, THE LENS IS THE CAMERA'S DISTANCE.
       S.focal.manualMM is read in exactly one place — the solve — and the solve
       needs a photo and four marks, so on this path the field sat there doing
       nothing at all. It is not meaningless here though, just misattached: a
       focal length and a subject size ARE a camera distance, so this drives the
       Camera object instead of a stored number nothing reads.
       Derived rather than mirrored, in both directions: the value shown is
       computed from where the Camera is, so dragging it updates this, and
       typing here moves it. Nothing is stored, so the two cannot disagree —
       and the distance persists anyway, as the Camera's own position. */
    const cam = cameraObj();
    if(cam)
      p2.body.appendChild(numRow('35 mm equiv.', lensFromDistance(cam.pos.z), .5, v=>{
        cam.pos.z = distanceFromLens(Math.max(1, v));
        syncTransform(cam); rebuildHelpers(); requestRender(); renderRight();
      }, 'mm', 'lensByHand'));
  }

  const go = el('button','ro__go', byHand ? 'Mark a photo in Calibrate' : 'Change in Calibrate');
  go.innerHTML += ' <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 8h8M8.5 4.5 12 8l-3.5 3.5"/></svg>';
  go.onclick = ()=>goStage('calib');
  p2.body.appendChild(go);
  p2.body.appendChild(el('p','note', byHand
    ? 'The same numbers Calibrate sets. There is no photo on this project, so the viewpoint is wherever you drag the Camera — and the lens is another way of saying the same thing: the distance at which this screen just fills a 35 mm frame. It moves the Camera straight out and back only; height and sideways stay where you put them.'
    : 'The same numbers Calibrate sets, editable from either place. The corner marks stay where they are — change the screen here and re-lock the angle to bring the viewpoint with it.'));
  R.appendChild(p2);
}

/* Setter, not a direct export-and-reassign — embedPhoto is read from other
   modules (services/presets.js, services/projects.js, both via ui/rails.js's
   re-export) as a live binding, but an ES module import binding is
   read-only from the importing side, so anything outside this file that
   wants to change it (ui/settings.js's Project section) has to go through
   this instead of assigning the imported name directly. */
export function setEmbedPhoto(v){ embedPhoto = v; }

/* Scrubbing the playhead rewrites the values the inspector is showing, so the
   number fields have to follow it. Only the VALUES — rebuilding the panels on
   every frame of a scrub would be both slow and hostile to anyone mid-edit,
   which is the same reason refreshInspectorValues exists for gizmo drags.
   Playback deliberately does not emit this: sixty DOM writes a second to
   show digits nobody can read is pure cost, so state/timeline.js fires it
   once when playback stops instead. */
on('anim:applied', () => refreshInspectorValues());
/* The Calibrate Camera rig moves the viewer by dragging (ui/camrig.js); the
   Viewer readout beside it is text rather than a bound input, so it is rebuilt
   when the drag ends rather than sixty times a second underneath it. */
on('eye:nudged', () => renderRight());

/* ── the inspector's width ───────────────────────────────────────────────────
   Same gesture the timeline dock offers on its top edge (ui/timeline-view.js's
   initTimelineResize), and the same manners: drag to size, double-click to hand
   the decision back to the stylesheet, remembered across sessions because a
   panel width is a working preference rather than anything about the project.

   The width lives in a custom property on .app, so the grid column and anything
   else keyed to --rail-w move together instead of being set element by element. */
const RAIL_W_KEY = 'illusio3d.rail-width';
const MIN_RAIL_W = 200;           // below this the two-column field rows stop fitting
const MAX_RAIL_FRAC = .5;

function applyRailWidth(px){
  const app = document.querySelector('.app');
  if(!app) return;
  if(px == null){ app.style.removeProperty('--rail-w'); return; }
  const max = Math.max(MIN_RAIL_W, Math.round(innerWidth * MAX_RAIL_FRAC));
  app.style.setProperty('--rail-w', clamp(Math.round(px), MIN_RAIL_W, max) + 'px');
}

function initRailResize(){
  const handle = $('#railResize');
  if(!handle) return;

  handle.addEventListener('pointerdown', ev=>{
    if(ev.button !== 0) return;
    ev.preventDefault();
    const startX = ev.clientX, startW = $('#railR').getBoundingClientRect().width;
    handle.setPointerCapture(ev.pointerId);
    handle.classList.add('on');
    let frame = 0;
    const move = e=>{
      /* The handle is on the rail's LEFT edge, so dragging left (clientX
         decreasing) has to WIDEN it — the opposite sign a handle on the other
         side would use. Same inversion the timeline's top-edge handle makes. */
      applyRailWidth(startW + (startX - e.clientX));
      /* Resizing the rail resizes the stage beside it, which is exactly what a
         window resize does — so let the existing listener do the work rather
         than half-repeating it here (the renderer, the photo fit and the
         calibration handles all hang off it). Throttled to one a frame, which
         is the cadence a real window drag produces anyway. */
      if(frame) return;
      frame = requestAnimationFrame(()=>{ frame = 0; dispatchEvent(new Event('resize')); });
    };
    const up = ()=>{
      handle.releasePointerCapture(ev.pointerId);
      handle.classList.remove('on');
      removeEventListener('pointermove', move);
      removeEventListener('pointerup', up);
      cancelAnimationFrame(frame);
      dispatchEvent(new Event('resize'));
      requestRender();
      try{
        const w = document.querySelector('.app').style.getPropertyValue('--rail-w');
        if(w) localStorage.setItem(RAIL_W_KEY, w); else localStorage.removeItem(RAIL_W_KEY);
      }catch(_){}
    };
    addEventListener('pointermove', move);
    addEventListener('pointerup', up);
  });

  handle.addEventListener('dblclick', ()=>{
    applyRailWidth(null);
    dispatchEvent(new Event('resize'));
    try{ localStorage.removeItem(RAIL_W_KEY); }catch(_){}
  });

  let saved = null;
  try{ saved = localStorage.getItem(RAIL_W_KEY); }catch(_){}
  if(saved) applyRailWidth(parseFloat(saved));
}
initRailResize();

export function refreshInspectorValues(){
  inspectorRefs.forEach(r=>{
    const v = r.target[r.key];
    if(typeof v === 'number' && document.activeElement !== r.input) r.input.value = fmt(v, r.d);
  });
}
/* Physical size now follows the pixel aspect ratio, not the other way round: a
   display's shape is its pixel grid, and metres are just how big you're printing
   or building it. Given a diagonal size and an aspect ratio (w:h in any units,
   e.g. straight from S.res), returns the metre width/height that satisfy both
   diagonal^2 = w^2+h^2 and w/h = aspectW/aspectH. */
export function metersFromDiagonalInches(inches, aspectW, aspectH){
  const diag = inches * 0.0254;
  const r = aspectW / Math.max(1e-9, aspectH);
  const h = diag / Math.sqrt(r*r + 1);
  return { w: h*r, h };
}
export function pxAspect(){ return S.res.w / Math.max(1, S.res.h); }

/* LENS ↔ DISTANCE, for the by-hand viewer (see "Describes the installation").
   A 35 mm-equivalent focal length is defined against the 36 × 24 mm still
   frame, so a focal length plus a subject size is a distance and nothing more
   is needed: the screen just fills the frame when

       d = W · f / 36        horizontally
       d = H · f / 24        vertically

   and FITTING it means the larger of the two — the tighter constraint wins, or
   the screen would be cropped on its other axis. Inverting takes the smaller
   for the same reason. Stated against S.screen, the panel the deliverable is
   rendered for; a corner screen's second panel recedes away from the viewer, so
   there is no single frame it is "in" and including it would only make the
   number wander as the corner angle changed. */
const GATE_W = 36, GATE_H = 24;
export function distanceFromLens(mm){
  return Math.max(S.screen.w * mm / GATE_W, S.screen.h * mm / GATE_H);
}
export function lensFromDistance(d){
  return Math.max(0, Math.min(GATE_W * d / Math.max(1e-6, S.screen.w),
                              GATE_H * d / Math.max(1e-6, S.screen.h)));
}

/* The screen size as of the last onScreenChange() call, so the next one can
   tell what actually changed — every caller (Pixels across/down, Width,
   Height, the Flip button, the Display-size-inches dropdown, doSolve's
   orientation auto-correct) already mutates S.screen directly before
   calling this, so by the time this runs the "before" value is gone unless
   something remembered it first. Starts at whatever S.screen already is at
   module load (the app's own default) rather than null, so even the FIRST
   screen edit of a fresh session has a real baseline to rescale existing
   objects against — a session can add objects (manual setup's starter
   recess, or more) before ever touching a screen-size field. Reset again
   by services/projects.js's applyDoc right after it sets S.screen —
   without that, opening a second, unrelated project would compare its
   screen size against whatever the PREVIOUS project's was and rescale
   every object in the new one for no reason. */
let lastScreen = { w: S.screen.w, h: S.screen.h };
export function noteScreenBaseline(){ lastScreen = { w: S.screen.w, h: S.screen.h }; }

export function onScreenChange(){
  /* Before the rescale below, not after: refitRecess reads the second panel's
     width, so it has to be the width this screen size implies. Same for an
     array's wall, which IS the screen everything below fits against. */
  syncArrayWall();
  syncCornerWidth();
  /* The calibrate footer asks a different question of an array than of a
     screen — see syncCalibFoot — and creating one is a screen change like any
     other, so it is refreshed from the one place they all pass through. */
  syncCalibFoot();
  /* The drawing follows the numbers on every path, not just the ones that
     rebuild the rail — the metre fields commit without a rebuild, and a shape
     that lags the fields describing it is worse than no shape at all. */
  syncPlan();
  // Proportionally rescale everything ALREADY on the scene before recess/
  // frame get refit to the new size below — see rescaleForScreen's own
  // comment for why light needs a different ratio than everything else,
  // and why recess/frame are excluded here (their own refit is exact,
  // not a proportional nudge).
  rescaleForScreen(lastScreen, S.screen);
  lastScreen = { w: S.screen.w, h: S.screen.h };

  S.objects.forEach(ob=>{
    if(ob.type==='recess' && ob.params.autoFill !== false){ refitRecess(ob); syncNode(ob); }
    if(ob.type==='frame'){ refitFrame(ob); syncNode(ob); }
  });
  rebuildHelpers();
  /* S.solved is also true for manual/no-photo setup (calibrate-view.js's
     manualSetup) — there are no corners there at all, so "corners moved, lock
     the angle again" doesn't mean anything and shouldn't fire just because the
     screen size changed. Only a real, photo-based calibration can go stale.
     An array's photo is not one; markStale itself now refuses that case, for
     every caller rather than only this one. */
  if(S.solved && S.photo) markStale();
  fitStage(); requestRender();
}
export function onResChange(){
  syncCornerWidth();
  syncPlan();
  fitStage(); requestRender();
}

/* THE SECOND PANEL IS SIZED IN PIXELS, AND ITS METRES FOLLOW.

   Content crosses the shared edge, so if the two panels are built at different
   px/m it changes size exactly where the eye is looking. Both numbers being
   typeable makes that mismatch the default outcome rather than a mistake.

   Which of the two to keep took a wrong turn first: the width in metres stayed
   the input and the pixel count was derived from it. That is backwards. A panel
   is bought and built as a pixel count — the metres are whatever that many
   pixels comes to at the pitch the first panel establishes — so metres was the
   input you could not actually answer, and the derived pixel count landed on
   values no product is made in. Pixels in, metres out, and the pitch matches by
   construction.

   The first panel keeps both of its own numbers: S.screen and S.res together
   ARE the pitch, and something has to define it. */
/* An array's wall IS S.screen, so it is derived here rather than typed — the
   same "one editable home" rule syncCornerWidth follows just below. The array
   spec (cols, rows, margin, one screen's size and pixels) is what you set; the
   wall those add up to is written back into screen/res so every existing reader
   — refitRecess, rescaleForScreen, the Flat camera, On-site — keeps seeing the
   single rectangle it always has.

   Called from onScreenChange, beside syncCornerWidth, so no caller has to know
   which kind of installation it is looking at. */
export function syncArrayWall(){
  const a = S.screenArray;
  if(!a) return;
  const wall = wallOf(a);
  S.screen.w = wall.w; S.screen.h = wall.h;
  S.res.w = wall.resW; S.res.h = wall.resH;
  /* AND RE-BASELINE, so the rescale further down onScreenChange sees no change
     and leaves the scene alone.

     onScreenChange proportionally rescales every object whenever the screen
     resizes, which is right when someone RETYPES a screen size: the design was
     made for a 6 m wall and should still fill a 3 m one. It is wrong here.
     Adding a row or widening the margin is describing the hardware — saying the
     wall is six screens rather than two — not asking for the artwork to be
     resized, and the objects were placed against the wall you can see.

     Left alone it was destructive rather than merely surprising: the ratio is
     taken from min(w,h), so changing ROWS (which moves the height, often the
     min) swung objects by 2x at a time while changing COLUMNS did nothing at
     all. A few edits while setting a grid up left an object far outside any
     size it had been given — reported as an image vanishing, outline and all,
     with no way to click it back. */
  noteScreenBaseline();
}

export function syncCornerWidth(){
  const c = S.cornerPanel;
  if(!c) return;
  /* Derived, always. A panel is bought and built as a pixel count, and its
     metres are whatever that many comes to at the pitch panel one sets — so
     metres was the input nobody could answer and the derived pixel count
     landed on values no product is made in.

     It was briefly typeable, to express a corner whose panels carry different
     pitches. That is a real installation, but it was added chasing a seam
     that turned out to be the warp rather than the width, so it bought a
     second editable number and fixed nothing. If it comes back it should come
     back for a corner that actually has one. */
  const density = S.res.w / Math.max(1e-6, S.screen.w);   // px per metre, panel one
  c.w = Math.max(.02, c.resW / Math.max(1e-6, density));
}
